import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { getAuth, GoogleAuthProvider, reauthenticateWithPopup } from "firebase/auth";
import { apiGetJson, apiPostJson } from "../../utils/apiClient";
import { StatusSelect } from "../../components/StatusSelect";
import { CsvCol, downloadCsv } from "../../utils/csvExport";
import dayjs from "dayjs";

interface CompanyRow {
  tenantId: string;
  companyId: string;
  shopUrl: string;
  enabledMalls?: string[];
  lastMonthSales?: number;
  lastMonthAdCost?: number;

  registeredAt?: any; // Firestore Timestamp / string / Date / number なども許容

  startDate?: string;
  endDate?: string;
  companyName?: string;
  shopName?: string;

  contractType?: string; // ✅ 追加：契約内容
  trialStatus?: string; // ✅ 既存を契約ステータスとして利用
  supportStatus?: string;
  plan?: string;
  dataFetch?: boolean;

  operationStatus?: string;
  notifyStatus?: string;
  note?: string;

  chatworkRoomId?: string;
  chatworkContactRoomId?: string; // ✅ 追加：チャットワーク連絡用

  memberCount?: number;

  // 自動取得エラー（APIから返ってくる想定）
  dataFetchErrCount?: number;
  dataFetchErrLastAt?: any; // Firestore Timestamp / string / Date / number
  dataFetchErrLastCode?: string;
  dataFetchErrLastMsg?: string;
  dataFetchLastOkAt?: any;
  dataFetchErrResolvedAt?: any;
  dataFetchErrResolvedBy?: string;

  // RMS認証ステータス（shops/{shopUrl}/settings/rms）
  rmsAuthStatus?: string | null;
  rmsAuthStatusUpdatedAt?: any;
  rmsAuthErrorMessage?: string;

  // RPP更新（RN-154。download の dataFetch 系の隣にそろえる）
  rppUpdateFlag?: boolean; // RPP更新の有効/無効
  // RPP更新の最新ログ（rmsRppUpdate が書き戻す read-only）
  rppUpdateErrCount?: number;
  rppUpdateErrLastAt?: any;
  rppUpdateErrLastMsg?: string;
  rppUpdateLastOkAt?: any;
  rppTaskSheetId?: string; // RPP更新タスクの設定スプレッドシートID

  // UI flags
  _dirty?: boolean;
  _saving?: boolean;
  _saveError?: string;

  _resolvingErr?: boolean;
  _resolveError?: string;
  _resolvingRmsAuth?: boolean;
  _resolveRmsAuthError?: string;
}

type RmsSecretKey = "rmsPass" | "rmsPass2" | "rmsPass3" | "rmsPass4" | "rmsPass5" | "csvPass" | "yahooRpaPass";

type RmsCredentialResponse = {
  rmsUser?: string;
  rmsPass?: string;
  rmsPass2?: string;
  rmsPass3?: string;
  rmsPass4?: string;
  rmsPass5?: string;
  csvUser?: string;
  csvPass?: string;
  // Yahoo RPA用ログイン（Secret Manager: ysc-login-<accountKey>）
  yahooRpaId?: string;
  yahooRpaPass?: string;
  // RPAログインアカウントの紐づけ（mall_config.ysc_shop.login_account_key）
  yahooAccountKey?: string;
};

type RmsCredentialDialogState = {
  open: boolean;
  companyId: string;
  shopUrl: string;
  tenantId: string;
  currentPassword: string; // 最小変更のため残す（未使用）
  busy: boolean;
  error: string;
  verified: boolean;
  credentials: RmsCredentialResponse | null;
  visibleSecrets: Record<RmsSecretKey, boolean>;
  copiedKey: string;
};

type BulkMailTarget = {
  tenantId: string;
  companyId: string;
  shopUrl: string;
  companyName?: string;
  shopName?: string;
  trialStatus?: string; // 正規化済み契約ステータス（ダイアログ内の絞り込み・解約済警告用）
  plan?: string;
};

type BulkMailPreviewRow = BulkMailTarget & {
  emails: string[];
  recipientNames?: Record<string, string>;
  status: "ok" | "skip";
  reason?: string;
};

type BulkMailDialogState = {
  open: boolean;
  loading: boolean;
  testSending: boolean;
  sending: boolean;
  error: string;

  subject: string;
  body: string;

  draftJobId: string;
  targets: BulkMailTarget[];
  statusFilters: string[]; // 契約ステータス絞り込み（空配列=絞り込みなし）
  planFilters: string[]; // プラン絞り込み（空配列=絞り込みなし）
  previewRows: BulkMailPreviewRow[];
  okCount: number;
  skipCount: number;
  recipientCount: number;

  confirmCountInput: string;
  tested: boolean;
};

const RMS_SECRET_KEYS: RmsSecretKey[] = ["rmsPass", "rmsPass2", "rmsPass3", "rmsPass4", "rmsPass5", "csvPass", "yahooRpaPass"];

function createEmptyVisibleSecrets(): Record<RmsSecretKey, boolean> {
  return {
    rmsPass: false,
    rmsPass2: false,
    rmsPass3: false,
    rmsPass4: false,
    rmsPass5: false,
    csvPass: false,
    yahooRpaPass: false,
  };
}

/**
 * 一斉メール（メルマガ）の件名に付ける識別タグ。
 * 利益Navi から送信されたメールであることを、受信者・社内の双方が判別できるようにする。
 */
const BULK_MAIL_SUBJECT_TAG = "【利益Navi】";

/** 件名に識別タグを必ず付ける（すでに付いていれば二重に付けない） */
function withBulkMailSubjectTag(subject: string): string {
  const s = String(subject ?? "").trim();
  if (!s) return BULK_MAIL_SUBJECT_TAG;
  return s.startsWith(BULK_MAIL_SUBJECT_TAG) ? s : `${BULK_MAIL_SUBJECT_TAG}${s}`;
}

function createInitialBulkMailDialogState(): BulkMailDialogState {
  return {
    open: false,
    loading: false,
    testSending: false,
    sending: false,
    error: "",
    subject: `${BULK_MAIL_SUBJECT_TAG}お知らせ`,
    body:
      "{{shopName}} ご担当者様\n\n" +
      "いつもご利用ありがとうございます。\n" +
      "ec vision からのお知らせです。\n\n" +
      "対象ショップURL: {{shopUrl}}\n\n" +
      "よろしくお願いいたします。\n" +
      "ec vision",
    draftJobId: "",
    targets: [],
    statusFilters: [],
    planFilters: [],
    previewRows: [],
    okCount: 0,
    skipCount: 0,
    recipientCount: 0,
    confirmCountInput: "",
    tested: false,
  };
}

// ===== Chatwork一斉投稿（担当者名義で連絡用ルームへ投稿。フローは一斉メールと同型） =====
type ChatworkSenderOption = {
  id: string;
  name: string;
  chatworkAccountName: string;
};

type BulkChatworkPreviewRow = BulkMailTarget & {
  roomId: string;
  plan: string;
  status: "ok" | "skip";
  reason?: string;
};

type BulkChatworkDialogState = {
  open: boolean;
  loading: boolean;
  testSending: boolean;
  sending: boolean;
  sendersLoading: boolean;
  error: string;

  senders: ChatworkSenderOption[];
  senderId: string;
  planOnly: boolean; // スタンダード相当プランのみに投稿（送信時選択）
  body: string;
  testRoomId: string;

  draftJobId: string;
  targets: BulkMailTarget[];
  previewRows: BulkChatworkPreviewRow[];
  okCount: number;
  skipCount: number;

  confirmCountInput: string;
  tested: boolean;
};

function createInitialBulkChatworkDialogState(): BulkChatworkDialogState {
  return {
    open: false,
    loading: false,
    testSending: false,
    sending: false,
    sendersLoading: false,
    error: "",
    senders: [],
    senderId: "",
    planOnly: true,
    body:
      "{{shopName}} ご担当者様\n\n" +
      "いつもお世話になっております。ec vision です。\n\n" +
      "（ここにお知らせ本文）\n\n" +
      "よろしくお願いいたします。",
    testRoomId:
      typeof window !== "undefined"
        ? localStorage.getItem("bulkChatworkTestRoomId") || ""
        : "",
    draftJobId: "",
    targets: [],
    previewRows: [],
    okCount: 0,
    skipCount: 0,
    confirmCountInput: "",
    tested: false,
  };
}

function maskSecretKeepLast4(v: any) {
  const s = String(v ?? "");
  if (!s) return "-";
  if (s.length <= 4) return "•".repeat(s.length);
  return `${"•".repeat(Math.max(4, s.length - 4))}${s.slice(-4)}`;
}

function formatBulkMailRecipients(row: BulkMailPreviewRow) {
  if (!Array.isArray(row.emails) || row.emails.length === 0) return "-";
  return row.emails
    .map((email) => {
      const name = String(row.recipientNames?.[email] ?? "").trim();
      return name ? `${name} <${email}>` : email;
    })
    .join("\n");
}

// RN-305: 「テスト」＝試し用のお店（既定で隠し・監視対象から外す。取得は続く）
const CONTRACT_TYPE_OPTIONS = ["", "利益Navi", "コンサル", "運用代行", "広告運用", "テスト"];
const CONTRACT_TYPE_COLORS: Record<string, string> = {
  利益Navi: "#E4666A",
  コンサル: "#3B82F6",
  運用代行: "#14B8A6",
  広告運用: "#F5CF82",
  テスト: "#9CA3AF",
};

const CONTRACT_STATUS_OPTIONS = ["", "トライアル", "無料", "有料", "解約予定", "解約済", "パートナー", "アンバサダー"];
const CONTRACT_STATUS_COLORS: Record<string, string> = {
  トライアル: "#E5E8EC",
  無料: "#A5D67A",
  有料: "#E97474",
  解約予定: "#8C4A13",
  解約済: "#4A4A4A",
  パートナー: "#8B5CF6",
  アンバサダー: "#F59E0B",
};

const PLAN_STATUS = ["", "フリープラン", "ライトプラン", "スタンダード", "プラスプラン", "プロ", "エンタープライズ"];
const PLAN_COLORS: Record<string, string> = {
  フリープラン: "#E5E8EC",
  ライトプラン: "#E4666A",
  スタンダード: "#C8DDF5",
  プラスプラン: "#A5D6C8",
  プロ: "#F5CF82",
  エンタープライズ: "#D2C6EA",
};

const OPERATION_STATUS = [
  "",
  "お申込み",
  "グループ作成済み",
  "スタッフ登録中",
  "スタッフ登録完了",
  "作成中",
  "入力レクチャー予定",
  "ツール入力依頼中",
  "正常に稼働中",
  "エラー確認中",
  "トライアル終了",
  "質問お待たせ中",
];
const OPERATION_COLORS: Record<string, string> = {
  お申込み: "#E4666A",
  グループ作成済み: "#C8DDF5",
  スタッフ登録中: "#BDE7C8",
  スタッフ登録完了: "#5EBB76",
  作成中: "#EDEAEA",
  入力レクチャー予定: "#D2C6EA",
  ツール入力依頼中: "#C8AFF7",
  正常に稼働中: "#A5D67A",
  エラー確認中: "#D9534F",
  トライアル終了: "#4B4B4B",
  質問お待たせ中: "#F5CF82",
};

const NOTICE_STATUS = ["", "未設定", "案内中", "通知確認", "通知エラー"];
const NOTICE_COLORS: Record<string, string> = {
  未設定: "#3A3A3A",
  案内中: "#AED4F3",
  通知確認: "#E2E2E2",
  通知エラー: "#D0383E",
};

const LOGIN_AUTH_ERROR_MESSAGE = "ログイン認証エラーが発生しました";
// 止まっている店舗を取得の入口で飛ばしたときの記録の文（RN-284）。
// apps/legacy/src/services/rms/core/browser/rlogin-credential-error.js の RMS_AUTH_STOPPED_MESSAGE と同じ文にする
const RMS_AUTH_STOPPED_MESSAGE = "RMS認証情報が無効のため取得を停止中です（初期設定で再登録が必要）";

// ✅ 旧データ互換用（以前 trialStatus に混在していた契約内容）
const LEGACY_CONTRACT_TYPE_VALUES = new Set(["コンサル", "運用代行", "広告運用", "利益Navi"]);

function normalizeContractStatusValue(v: any): string {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (s === "解約") return "解約済"; // 旧値吸収
  if (LEGACY_CONTRACT_TYPE_VALUES.has(s)) return "";
  return s;
}

function getContractTypeValue(c: CompanyRow): string {
  const v = String(c.contractType ?? "").trim();
  if (v) return v;

  const legacy = String(c.trialStatus ?? "").trim();
  if (LEGACY_CONTRACT_TYPE_VALUES.has(legacy)) return legacy;

  return "";
}

// ✅ 情報量が多いのでパディング/マージンは小さめ
const thBase = "border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-xs";
const tdBase = "border px-2 py-1 text-xs";
const AVAILABLE_MALLS = ["rakuten", "amazon", "yahoo"] as const;
const MALL_LABELS: Record<(typeof AVAILABLE_MALLS)[number], string> = {
  rakuten: "楽天",
  amazon: "Amazon",
  yahoo: "Yahoo!",
};

type SortKey =
  | "tenantId"
  | "shopUrl"
  | "registeredAt"
  | "companyName"
  | "shopName"
  | "contractType"
  | "trialStatus"
  | "memberCount"
  | "operationStatus"
  | "plan"
  | "dataFetchErrCount"
  | "dataFetchErrLastAt"
  | "dataFetch";

// RPP更新の最新ログを3分類（error/ok/none）に正規化する。CSVとテーブルセルで分類を共有し、
// 表示（色・truncate）は各々で行う。
type RppStatus = { kind: "error" | "ok" | "none"; at: string; msg: string };
function rppStatusParts(c: CompanyRow): RppStatus {
  if ((c.rppUpdateErrCount ?? 0) > 0) {
    return { kind: "error", at: toYmdHm(c.rppUpdateErrLastAt) || "", msg: c.rppUpdateErrLastMsg || "" };
  }
  if (c.rppUpdateLastOkAt) {
    return { kind: "ok", at: toYmdHm(c.rppUpdateLastOkAt) || "", msg: "" };
  }
  return { kind: "none", at: "", msg: "" };
}

function companyRowKey(c: Pick<CompanyRow, "tenantId" | "companyId" | "shopUrl">): string {
  return `${String(c.tenantId ?? "")}||${String(c.companyId ?? "")}||${String(c.shopUrl ?? "")}`;
}

function dedupeCompanies(rows: CompanyRow[]): CompanyRow[] {
  const seen = new Set<string>();
  const out: CompanyRow[] = [];
  for (const row of rows) {
    const key = companyRowKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function isBlank(v: any) {
  return v == null || String(v).trim() === "";
}

function normalizeEnabledMalls(v: any): string[] {
  const src = Array.isArray(v) ? v : [];
  const normalized = src
    .map((x) => String(x ?? "").trim().toLowerCase())
    .filter((x) => AVAILABLE_MALLS.includes(x as any));
  const unique = Array.from(new Set(normalized));
  return unique.length > 0 ? unique : ["rakuten"];
}

/** Firestore Timestamp / {seconds,nanoseconds} / {_seconds,_nanoseconds} / Date / number(ms|sec) / 文字列 を Date に寄せる */
function parseDateAny(v: any): Date | null {
  if (!v) return null;

  // Date
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

  // Firestore Timestamp (client) has toDate()
  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      // ignore
    }
  }

  // Timestamp-like object
  const sec = v?.seconds ?? v?._seconds;
  const nsec = v?.nanoseconds ?? v?._nanoseconds;
  if (typeof sec === "number") {
    const ms = sec * 1000 + (typeof nsec === "number" ? Math.floor(nsec / 1_000_000) : 0);
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // number (ms or seconds)
  if (typeof v === "number") {
    const ms = v < 1_000_000_000_000 ? v * 1000 : v; // 秒っぽければmsへ
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // string
  const s = String(v).trim();
  if (!s) return null;

  // ISO
  const isoTry = new Date(s);
  if (!Number.isNaN(isoTry.getTime())) return isoTry;

  // "YYYY/MM/DD HH:mm:ss.SSS" や "YYYY-MM-DD HH:mm:ss" など
  const m = s.match(
    /^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/
  );
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const hh = m[4] != null ? Number(m[4]) : 0;
    const mm = m[5] != null ? Number(m[5]) : 0;
    const ss2 = m[6] != null ? Number(m[6]) : 0;
    const ms2 = m[7] != null ? Number(String(m[7]).padEnd(3, "0")) : 0;
    const dt = new Date(y, mo, d, hh, mm, ss2, ms2);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
}

function toMillis(v: any): number | null {
  const d = parseDateAny(v);
  return d ? d.getTime() : null;
}

function toYmd(v: any) {
  const d = parseDateAny(v);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toYmdHm(v: any) {
  const d = parseDateAny(v);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

function shortText(v: any, max = 60) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

// RN-285: 取得停止の経緯（GET /api/super/companies/:tenantId/:companyId/:shopUrl/history の1行）
type RmsHistoryItem = {
  id: string;
  type: string;
  at?: string;
  companyId?: string | null;
  byEmail?: string | null;
  bySuperAdmin?: boolean;
  reason?: string | null;
  message?: string | null;
  kind?: string | null;
  claim?: string | null;
  chatwork?: string | null;
  room?: string | null;
  mail?: string | null;
  failed?: boolean;
  prevInvalid?: boolean;
  clearedFetchError?: boolean;
  prevStatus?: string | null;
  prevMessage?: string | null;
  prevCount?: number | null;
  prevLastOkAt?: string | null;
  daysSinceLastOk?: number | null;
};

function rmsHistoryTitle(it: RmsHistoryItem): string {
  const auth = it.reason === "order" || it.kind === "order" ? "受注データの認証" : "R-Login";
  switch (it.type) {
    case "rmsAuthStopped":
      return `止まった（${auth}）`;
    case "rmsAuthNotice":
      return `知らせ（${auth}）`;
    case "rmsCredentialsSaved":
      return it.bySuperAdmin ? "初期設定で保存（運営）" : "初期設定で保存（お客様）";
    case "dataFetchErrResolved":
      return "対応済み（運営）";
    case "rmsAuthUnlocked":
      return "解除（運営）";
    case "rmsAuthResumed":
      return "再開";
    default:
      return it.type;
  }
}

// RN-286: 知らせの行の「ひとこと」は「○○だったので、○○した。「お店に届く知らせの題」」の形で、どこに・何を送ったかを書く
// お店に届く知らせの題（apps/legacy の utils/notification の AUTH_TEMPLATES の title から [SaaS][RPA] を除いたもの）
function rmsNoticeTitleQuoted(it: RmsHistoryItem): string {
  return it.kind === "order" || it.reason === "order" ? "「楽天RMS受注データダウンロード認証エラー」" : "「楽天RMSログインエラー」";
}

// 送らなかった理由（「〜通知は送らない」に続く形）
const RMS_NOTICE_NOT_SENT_BECAUSE: Record<string, string> = {
  "skipped:plan": "無料・ライトのため",
  "skipped:weekend": "土日のため",
  "skipped:quiet_hours": "夜間のため",
  "skipped:switch_off": "送る設定が切のため",
  "skipped:prod_send_off": "本番へ送る設定が切のため",
  "skipped:fetch_error_disabled": "お店の通知設定が切のため",
  "skipped:no_recipients": "宛先が無いため",
};

// 平日の昼にあとから送る知らせ（claim "deferred"）で、送れなかったときの理由（「〜ので送れなかった」に続く形）
const RMS_DEFERRED_NOT_SENT_REASONS: Record<string, string> = {
  "skipped:switch_off": "送る設定が切になっていた",
  "skipped:prod_send_off": "本番へ送る設定が切になっていた",
  "skipped:fetch_error_disabled": "お店の通知設定が切になっていた",
  "skipped:quiet_hours": "夜間だった",
  "skipped:weekend": "土日だった",
  failed: "Chatwork が受け付けなかった（ルームの番号・送る権限・Chatwork 側の不調のどれか）",
};

function rmsDeferredNoticeDetail(it: RmsHistoryItem): string {
  const what = rmsNoticeTitleQuoted(it);
  if (it.chatwork === "sent" && !it.failed) {
    return `取得が止まった際に通知を送っていなかったが、平日の昼の取得時にお店の Chatwork ルームへ全員宛で通知を送った。${what}`;
  }
  const lead = "取得が止まった際に通知を送っていなかったため、平日の昼の取得時にお店の Chatwork ルームへ通知を送ろうとしたが、";
  if (it.failed) return `${lead}途中でエラーになった（届いたかは分からない）。${what}`;
  const why = (it.chatwork && RMS_DEFERRED_NOT_SENT_REASONS[it.chatwork]) || "理由が分からない";
  return `${lead}${why}ので送れなかった。${what}`;
}

// 止まったときに送った知らせ（claim "claimed"）と、送らなかった知らせ（claim "skipped:…"）
function rmsNoticeDetail(it: RmsHistoryItem): string {
  const what = rmsNoticeTitleQuoted(it);
  // 受注データの認証の失敗では取得は止まらない（R-Login の誤りのときだけ止まる＝ data-download-worker.js の shouldSetInvalid）
  const happened = it.kind === "order" || it.reason === "order" ? "受注データのダウンロードの認証に失敗した" : "取得が止まった";
  if (it.claim === "skipped:claimed_today") {
    // 1日1回の印を RPP の更新と共有するのは R-Login の知らせだけ（rmsRppUpdate は kind "login" で取る）
    const rpp = it.kind === "order" || it.reason === "order" ? "" : "（RPPの更新の通知を含む）";
    return `${happened}が、今日はすでに通知を送っていたため送らない${rpp}。${what}`;
  }
  if (it.claim === "skipped:no_notice_path") {
    return `${happened}が、取得の途中の失敗のため、通知を送る作りが無い。${what}`;
  }
  if (it.claim && it.claim !== "claimed") {
    const because = RMS_NOTICE_NOT_SENT_BECAUSE[it.claim];
    return because ? `${happened}が、${because}通知は送らない。${what}` : `${happened}が、通知は送らない（${it.claim}）。${what}`;
  }
  if (it.failed) return `${happened}ので通知を送ろうとしたが、途中でエラーになった（Chatwork に届いたかは分からない）。${what}`;

  const sent: string[] = [];
  const notSent: string[] = [];
  if (it.chatwork === "sent") sent.push(`${it.room === "shop" ? "お店" : "運営"}の Chatwork ルームへ通知`);
  else if (it.chatwork === "failed") notSent.push("Chatwork は受け付けられず届かなかった（ルームの番号・送る権限・Chatwork 側の不調のどれか）");
  else if (it.chatwork) notSent.push(`Chatwork は${RMS_NOTICE_NOT_SENT_BECAUSE[it.chatwork] || `（${it.chatwork}）`}送らない`);
  // メールの宛先はお店の通知先（shopNotifications.default.emails）
  if (it.mail === "sent") sent.push("お店の通知先へメール");
  else if (it.mail === "sent_test") sent.push("テストの宛先へメール");
  else if (it.mail === "failed") notSent.push("メールは送れなかった");
  // メールを使わない設定（既定）と、Chatwork の送る設定が切で手前で止まったときは、メールのことは書かない（平常時は静かに）
  else if (it.mail && it.mail !== "skipped:switch_off" && it.mail !== "skipped:not_reached") {
    notSent.push(`メールは${RMS_NOTICE_NOT_SENT_BECAUSE[it.mail] || `（${it.mail}）`}送らない`);
  }

  const head = sent.length > 0 ? `${happened}ので、${sent.join("、")}を送った` : `${happened}が、通知は送っていない`;
  return `${head}${notSent.length > 0 ? `（${notSent.join("・")}）` : ""}。${what}`;
}

// RN-286: 試しの運転（RMS_AUTH_DEFERRED_NOTICE=dry）の記録は、開発の確かめ用のため経緯には出さない
function isDeferredDryNotice(it: RmsHistoryItem): boolean {
  return it.type === "rmsAuthNotice" && it.claim === "deferred:dry";
}

function rmsHistoryDetail(it: RmsHistoryItem): string {
  switch (it.type) {
    case "rmsAuthStopped":
      return it.message || "";
    case "rmsAuthNotice": {
      if (it.claim === "deferred") return rmsDeferredNoticeDetail(it);
      return rmsNoticeDetail(it);
    }
    case "rmsCredentialsSaved":
      return it.prevInvalid ? "止まっている状態で保存（次の取得で再開を確かめる）" : "";
    case "dataFetchErrResolved":
      return it.prevMessage ? `消した記録：${it.prevMessage}` : "";
    case "rmsAuthUnlocked":
      return it.prevStatus === "invalid" ? "INVALID を解除（取り直しが成功するまで、お客様の警告は残る）" : `解除の前：${it.prevStatus || "未判定"}`;
    case "rmsAuthResumed":
      if (it.prevLastOkAt) {
        const days = typeof it.daysSinceLastOk === "number" ? `（${it.daysSinceLastOk}日ぶり）` : "";
        return `最後に取れた日時 ${toYmdHm(it.prevLastOkAt)} から取得を再開${days}`;
      }
      return "取得を再開";
    default:
      return "";
  }
}

function isErrActive(c: CompanyRow) {
  const errCount = typeof c.dataFetchErrCount === "number" ? c.dataFetchErrCount : 0;
  if (errCount <= 0) return false;
  // resolvedAt がある場合、lastAt が resolvedAt より新しいなら未解決扱い
  const lastMs = toMillis(c.dataFetchErrLastAt);
  const resMs = toMillis(c.dataFetchErrResolvedAt);
  if (!resMs) return true;
  if (!lastMs) return true;
  return lastMs > resMs;
}

function isLoginAuthErrorActive(c: CompanyRow) {
  if (!isErrActive(c)) return false;
  // RMS認証が VALID（ログインに成功した／お客様が保存した）なら、止まっている文が残っていても数えない
  // （お客様の画面の判定 apps/web/src/utils/rmsStop.ts の isRmsStoppedFrom と同じ決まり）
  if (String(c.rmsAuthStatus ?? "").trim().toLowerCase() === "valid") return false;
  const msg = String(c.dataFetchErrLastMsg ?? "");
  return msg.includes(LOGIN_AUTH_ERROR_MESSAGE) || msg.includes(RMS_AUTH_STOPPED_MESSAGE);
}

function isRmsAuthInvalid(c: CompanyRow) {
  return String(c.rmsAuthStatus ?? "").trim().toLowerCase() === "invalid";
}

// RN-280: エラーの種類（楽天の自動取得のエラーだけ。1店舗は1つの種類に入る）
//   stopped＝お店の ID・パスワードでログインできず、仕組みが自動で取得を止めている（RMS の認証が無効）／
//   login＝ログインのエラーが出ているが、パスワードの誤りとは判定されていない（次の取得でまた試す）／
//   other＝ログイン以外の取得エラーで、運営が「対応済み」を押していない（「対応済み」のあと新しいエラーが出ていないお店は、いま問題が無いので選択肢に入れない）
type ErrKind = "stopped" | "login" | "other";
const ERR_KIND_LABELS: Record<ErrKind, string> = {
  stopped: "データ取得停止中（RMSの認証無効）",
  login: "ログインエラー（次の取得で再度試す）",
  other: "その他の取得エラーが続いている",
};
function errKindOf(c: CompanyRow): ErrKind | null {
  if (isRmsAuthInvalid(c)) return "stopped";
  if (isLoginAuthErrorActive(c)) return "login";
  if (isErrActive(c)) return "other";
  return null;
}

// RN-280: 監視対象（同期ヘルスの「監視対象」と同じ決め方）
// RN-292: 決まりは apps/api/src/services/monitoredShop.ts の isMonitoredShop と同じ式（画面は別の箱なので、ここに同じ式を書く）
//   データ取得が ON ／ 契約ステータスが解約済・解約予定でない（古い「解約」は解約済）／ プランがフリー・ライトでない（未設定はフリー扱い）
const MONITOR_EXCLUDE_PLANS = ["フリープラン", "ライトプラン"];
const MONITOR_EXCLUDE_TRIAL_STATUSES = ["解約済", "解約予定"];
// RN-305: 契約内容が「テスト」のお店（apps/api/src/services/monitoredShop.ts の isTestShop と同じ決まり）。
//   画面は既定で隠し（上のカードの数にも入れない）、「テスト店舗を表示」で出す。監視対象からも外す
const TEST_CONTRACT_TYPE = "テスト";
function isTestCompany(c: CompanyRow): boolean {
  return String(c.contractType ?? "").trim() === TEST_CONTRACT_TYPE;
}
function isMonitored(c: CompanyRow): boolean {
  const plan = String(c.plan ?? "").trim() || "フリープラン";
  const rawStatus = String(c.trialStatus ?? "").trim();
  const trialStatus = rawStatus === "解約" ? "解約済" : rawStatus;
  return (
    c.dataFetch === true &&
    !isTestCompany(c) &&
    !MONITOR_EXCLUDE_TRIAL_STATUSES.includes(trialStatus) &&
    !MONITOR_EXCLUDE_PLANS.includes(plan)
  );
}

// RN-280: 絞り込みのプルダウン（同じ見た目・同じ大きさ）。いくつでも選べる形と、1つだけ選ぶ形（single）がある。
//   options の group で見出しを分け（例：楽天／Amazon／Yahoo!）、disabled の行は選べない（準備中）
//   メニューは表の見出し（固定の列 z-40）より手前に出す（z-[60]）
const FILTER_BUTTON_CLASS = "w-[12rem] border border-sky-400 rounded px-2 py-1 bg-sky-50 text-sky-900 text-left hover:bg-sky-100";
function MultiSelectDropdown(props: {
  label: string;
  options: { value: string; label: string; count?: number; group?: string; disabled?: boolean }[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  onSelectAll?: () => void;
  single?: boolean;
}) {
  const { label, options, selected, onToggle, onClear, onSelectAll, single } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const summary = single
    ? labelOf(selected[0] ?? "")
    : selected.length === 0
      ? "すべて"
      : selected.length === 1
        ? labelOf(selected[0])
        : `${selected.length}つ選択中`;
  const active = single ? selected.length > 0 && selected[0] !== options[0]?.value : selected.length > 0;
  let lastGroup: string | undefined;
  return (
    <div className="relative flex items-center" ref={ref}>
      <span className="font-semibold text-slate-800 mr-1 whitespace-nowrap">{label}：</span>
      <button
        type="button"
        className={`${FILTER_BUTTON_CLASS} ${active ? "border-sky-700 bg-sky-100 font-semibold" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={summary}
      >
        <span className="block truncate pr-4">{summary}</span>
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sky-700">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-[60] mt-1 min-w-[16rem] rounded border bg-white p-2 shadow-lg">
          {options.map((o) => {
            const head = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group ?? lastGroup;
            return (
              <React.Fragment key={`${o.group ?? ""}:${o.value || "(未設定)"}`}>
                {head && <div className="mt-1 first:mt-0 px-1 text-[11px] font-semibold text-slate-500">{head}</div>}
                <label
                  className={`flex items-center gap-2 px-1 py-1 ${o.disabled ? "cursor-not-allowed text-slate-400" : "cursor-pointer hover:bg-slate-50"}`}
                >
                  <input
                    type={single ? "radio" : "checkbox"}
                    disabled={o.disabled}
                    checked={selected.includes(o.value)}
                    onChange={() => {
                      onToggle(o.value);
                      if (single) setOpen(false);
                    }}
                  />
                  <span className="flex-1">{o.label}</span>
                  {typeof o.count === "number" && <span className="text-slate-500">{o.count}</span>}
                </label>
              </React.Fragment>
            );
          })}
          <div className="mt-1 flex justify-between border-t pt-1">
            {!single && (onClear || onSelectAll) ? (
              <span className="flex gap-3">
                {onClear && (
                  <button type="button" className="text-slate-600 underline" onClick={onClear}>
                    すべて選択を外す
                  </button>
                )}
                {onSelectAll && (
                  <button type="button" className="text-slate-600 underline" onClick={onSelectAll}>
                    すべて選択する
                  </button>
                )}
              </span>
            ) : (
              <span />
            )}
            <button type="button" className="text-slate-600 underline" onClick={() => setOpen(false)}>
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// RN-280: 「操作 ▼」のメニュー（書き出す・お客様へ送る・データを直す）。2回押さないと動かないので、誤って押しにくい
function OperationsMenu(props: {
  targetCount: number;
  busyLabel: string | null;
  groups: { title: string; warn?: boolean; items: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] }[];
}) {
  const { targetCount, busyLabel, groups } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div className="relative flex items-center gap-2" ref={ref}>
      <span className="font-semibold text-slate-800 whitespace-nowrap">操作：</span>
      <button
        type="button"
        className="relative w-[12rem] rounded border border-slate-900 px-2 py-1 bg-slate-800 text-white text-left whitespace-nowrap hover:bg-slate-700"
        onClick={() => setOpen((v) => !v)}
      >
        書き出す・送る・直す
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">▾</span>
      </button>
      {busyLabel && <span className="text-slate-600 whitespace-nowrap">{busyLabel}</span>}
      {open && (
        <div className="absolute left-0 top-full z-[60] mt-1 w-[17rem] rounded border bg-white p-2 shadow-lg text-xs">
          {groups.map((g) => (
            <div key={g.title} className={`mb-2 last:mb-0 rounded p-1 ${g.warn ? "border border-amber-300 bg-amber-50" : ""}`}>
              <div className={`mb-1 text-[11px] ${g.warn ? "font-semibold text-amber-800" : "text-slate-500"}`}>
                {g.warn ? "⚠ " : ""}
                {g.title}
              </div>
              {g.items.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  className="block w-full rounded px-2 py-1 text-left text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                  disabled={it.disabled}
                  title={it.title}
                  onClick={() => {
                    setOpen(false);
                    it.onClick();
                  }}
                >
                  {it.label}
                </button>
              ))}
            </div>
          ))}
          <div className="border-t pt-1 text-[11px] text-slate-500">対象は「表示中」の {targetCount} 件です</div>
        </div>
      )}
    </div>
  );
}

export default function SuperCompanyListPage() {
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [search, setSearch] = useState("");
  // RN-280: 表示の大きさ（初めはコンパクト＝ブラウザの 80% と同じ見え方）。見る人のブラウザに覚えさせる
  const [density, setDensity] = useState<"compact" | "normal">(() => {
    try {
      return localStorage.getItem("superCompanyListDensity") === "normal" ? "normal" : "compact";
    } catch {
      return "compact";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("superCompanyListDensity", density);
    } catch {
      // 覚えられなくても表示には困らない
    }
  }, [density]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // ✅ 変更：RMS認証情報取得中（1件だけでも十分）
  const [proxyBusyKey, setProxyBusyKey] = useState<string>("");

  // 一括補完UI
  const [fillLoading, setFillLoading] = useState(false);
  const [fillMsg, setFillMsg] = useState("");

  // syncSupportSheet
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  // ✅ エラー抽出（フィルタ）
  // RN-280: エラーの種類で絞り込む（いくつでも選べる。空＝すべて）
  const [errKinds, setErrKinds] = useState<ErrKind[]>([]);
  // RN-280: 監視対象だけを表示する（カードを押して切り替える）
  const [monitoredOnly, setMonitoredOnly] = useState(false);
  // RN-305: テストのお店も表示するか。保存しない＝開くたびに隠れた状態から始まる（毎回隠す手間を作らない）
  const [showTest, setShowTest] = useState(false);

  // RN-280: 「除外」（解約済を除く／解約・パートナー・コンサルを除く）は消した。
  //   同じ絞り込みは、契約ステータス・契約内容のプルダウンで「すべて選択する」→ 外したいものだけチェックを外す、でできる

  type SummaryFilterKind = "contractType" | "trialStatus" | "plan";

  type SummaryFilterItem = {
    kind: SummaryFilterKind;
    value: string;
  };

  const [summaryFilters, setSummaryFilters] = useState<SummaryFilterItem[]>([]);

  // ✅ 追加：RMS認証情報ダイアログ
  // Yahoo RPAログインアカウント紐づけの編集用
  const [yahooAccountKeyInput, setYahooAccountKeyInput] = useState("");
  const [yahooAccountSaving, setYahooAccountSaving] = useState(false);

  const [rmsCredDlg, setRmsCredDlg] = useState<RmsCredentialDialogState>({
    open: false,
    companyId: "",
    shopUrl: "",
    tenantId: "",
    currentPassword: "",
    busy: false,
    error: "",
    verified: false,
    credentials: null,
    visibleSecrets: createEmptyVisibleSecrets(),
    copiedKey: "",
  });

  // ✅ 追加：一斉メールダイアログ
  const [bulkMailDlg, setBulkMailDlg] = useState<BulkMailDialogState>(createInitialBulkMailDialogState());

  // ✅ 追加：Chatwork一斉投稿ダイアログ
  const [bulkChatworkDlg, setBulkChatworkDlg] = useState<BulkChatworkDialogState>(
    createInitialBulkChatworkDialogState()
  );

  // RN-285: 取得停止の経緯の小窓
  const [historyDlg, setHistoryDlg] = useState<{
    open: boolean;
    companyId: string;
    shopUrl: string;
    companyName: string;
    loading: boolean;
    error: string;
    items: RmsHistoryItem[];
  }>({ open: false, companyId: "", shopUrl: "", companyName: "", loading: false, error: "", items: [] });

  const [deleteDlg, setDeleteDlg] = useState<{
    open: boolean;
    companyId: string;
    shopUrl: string;
    tenantId: string;
    input: string;
    busy: boolean;
    error: string;
  }>({
    open: false,
    companyId: "",
    shopUrl: "",
    tenantId: "",
    input: "",
    busy: false,
    error: "",
  });

  const revealTimerRef = useRef(new Map<string, number>());
  const copiedTimerRef = useRef<number | null>(null);

  const openDelete = useCallback((companyId: string, shopUrl: string, tenantId: string) => {
    setDeleteDlg({
      open: true,
      companyId,
      shopUrl,
      tenantId,
      input: "",
      busy: false,
      error: "",
    });
  }, []);

  const closeDelete = useCallback(() => {
    setDeleteDlg((p) => ({ ...p, open: false, busy: false, error: "", input: "" }));
  }, []);

  const clearRevealTimers = useCallback(() => {
    revealTimerRef.current.forEach((t) => window.clearTimeout(t));
    revealTimerRef.current.clear();
  }, []);

  const openRmsCredentialDialog = useCallback((companyId: string, shopUrl: string, tenantId: string) => {
    setError("");
    setRmsCredDlg({
      open: true,
      companyId,
      shopUrl,
      tenantId,
      currentPassword: "",
      busy: false,
      error: "",
      verified: false,
      credentials: null,
      visibleSecrets: createEmptyVisibleSecrets(),
      copiedKey: "",
    });
  }, []);

  const closeRmsCredentialDialog = useCallback(() => {
    clearRevealTimers();
    if (copiedTimerRef.current) {
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
    setRmsCredDlg({
      open: false,
      companyId: "",
      shopUrl: "",
      tenantId: "",
      currentPassword: "",
      busy: false,
      error: "",
      verified: false,
      credentials: null,
      visibleSecrets: createEmptyVisibleSecrets(),
      copiedKey: "",
    });
    setProxyBusyKey("");
  }, [clearRevealTimers]);

  const closeBulkMailDialog = useCallback(() => {
    setBulkMailDlg((prev) => ({
      ...createInitialBulkMailDialogState(),
      subject: prev.subject,
      body: prev.body,
    }));
  }, []);

  const invalidateBulkMailDraft = useCallback((patch: Partial<Pick<BulkMailDialogState, "subject" | "body">>) => {
    setBulkMailDlg((prev) => ({
      ...prev,
      ...patch,
      draftJobId: "",
      previewRows: [],
      okCount: 0,
      skipCount: 0,
      recipientCount: 0,
      confirmCountInput: "",
      tested: false,
      error: "",
    }));
  }, []);

  // ===== Auto-save infra =====
  const keyOf = (companyId: string, shopUrl: string) => `${companyId}||${shopUrl}`;
  const latestRowRef = useRef(new Map<string, CompanyRow>());
  const timerRef = useRef(new Map<string, number>());
  const changeSeqRef = useRef(new Map<string, number>());
  const saveSeqRef = useRef(new Map<string, number>());

  const apiPathForShop = (tenantId: string, companyId: string, shopUrl: string) =>
    `/api/super/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}/${encodeURIComponent(shopUrl)}`;

  const deleteApiPathForShop = (tenantId: string, companyId: string, shopUrl: string) =>
    `/api/super/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(
      companyId
    )}/${encodeURIComponent(shopUrl)}/delete`;

  const runDelete = useCallback(async () => {
    const { tenantId, companyId, shopUrl, input, busy } = deleteDlg;
    if (busy) return;

    if (String(input).trim() !== String(shopUrl).trim()) {
      setDeleteDlg((p) => ({ ...p, error: "ショップURLが一致しません" }));
      return;
    }

    setDeleteDlg((p) => ({ ...p, busy: true, error: "" }));
    try {
      await apiPostJson(deleteApiPathForShop(tenantId, companyId, shopUrl), {
        confirmShopUrl: input,
      });

      const key = keyOf(companyId, shopUrl);

      setCompanies((prev) => prev.filter((c) => !(c.companyId === companyId && c.shopUrl === shopUrl)));

      latestRowRef.current.delete(key);
      const t = timerRef.current.get(key);
      if (t) window.clearTimeout(t);
      timerRef.current.delete(key);
      changeSeqRef.current.delete(key);
      saveSeqRef.current.delete(key);

      closeDelete();
    } catch (e: any) {
      setDeleteDlg((p) => ({ ...p, busy: false, error: e?.message ?? "削除に失敗しました" }));
    }
  }, [deleteDlg, closeDelete]);

  const onDeleteInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        runDelete();
      }
    },
    [runDelete]
  );

  const flushSave = useCallback(async (companyId: string, shopUrl: string) => {
    const key = keyOf(companyId, shopUrl);

    const row = latestRowRef.current.get(key);
    if (!row || !row._dirty) return;

    const seqAtStart = changeSeqRef.current.get(key) ?? 0;
    const saveSeq = (saveSeqRef.current.get(key) ?? 0) + 1;
    saveSeqRef.current.set(key, saveSeq);

    setCompanies((prev) =>
      prev.map((c) =>
        c.companyId === companyId && c.shopUrl === shopUrl ? { ...c, _saving: true, _saveError: "" } : c
      )
    );

    try {
      const {
        companyId: cid,
        shopUrl: su,

        // 返却専用/表示専用
        memberCount,
        registeredAt,
        dataFetchErrCount,
        dataFetchErrLastAt,
        dataFetchErrLastCode,
        dataFetchErrLastMsg,
        dataFetchLastOkAt,
        dataFetchErrResolvedAt,
        dataFetchErrResolvedBy,
        rmsAuthStatus,
        rmsAuthStatusUpdatedAt,
        rmsAuthErrorMessage,

        // UI flags
        _dirty,
        _saving,
        _saveError,
        _resolvingErr,
        _resolveError,
        _resolvingRmsAuth,
        _resolveRmsAuthError,

        ...body
      } = row;

      await apiPostJson(apiPathForShop(row.tenantId, cid, su), body);

      if (saveSeqRef.current.get(key) !== saveSeq) return;

      const seqNow = changeSeqRef.current.get(key) ?? 0;
      const canClearDirty = seqNow === seqAtStart;

      setCompanies((prev) =>
        prev.map((c) =>
          c.companyId === companyId && c.shopUrl === shopUrl
            ? { ...c, _saving: false, _saveError: "", _dirty: canClearDirty ? false : true }
            : c
        )
      );
    } catch (e: any) {
      if (saveSeqRef.current.get(key) !== saveSeq) return;

      setCompanies((prev) =>
        prev.map((c) =>
          c.companyId === companyId && c.shopUrl === shopUrl
            ? { ...c, _saving: false, _saveError: e?.message ?? "保存に失敗しました" }
            : c
        )
      );
    }
  }, []);

  const queueSaveInternal = useCallback(
    (companyId: string, shopUrl: string, delayMs: number) => {
      const key = keyOf(companyId, shopUrl);

      const prevTimer = timerRef.current.get(key);
      if (prevTimer) window.clearTimeout(prevTimer);

      const t = window.setTimeout(() => {
        flushSave(companyId, shopUrl);
      }, Math.max(0, delayMs));

      timerRef.current.set(key, t);
    },
    [flushSave]
  );

  useEffect(() => {
    return () => {
      timerRef.current.forEach((t) => window.clearTimeout(t));
      timerRef.current.clear();
      clearRevealTimers();
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    };
  }, [clearRevealTimers]);

  // ===== Data load =====
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetJson("/api/super/companies");
      const rows = dedupeCompanies(Array.isArray(data) ? (data as CompanyRow[]) : []);
      setCompanies(rows);
      rows.forEach((r) => latestRowRef.current.set(keyOf(r.companyId, r.shopUrl), r));
    } catch (e: any) {
      setError(e?.message ?? "企業一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // syncSupportSheet
  const runSyncSupportSheet = useCallback(async () => {
    setError("");
    setFillMsg("");
    setSyncMsg("");

    setSyncLoading(true);
    try {
      const r = await apiPostJson("/api/super/sync-support-sheet", {});
      const msg = `SupportSheet同期: total=${r?.total ?? "-"} / updated=${r?.updated ?? "-"} / created=${r?.created ?? "-"} / skipped=${r?.skipped ?? "-"}`;
      setSyncMsg(msg);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "SupportSheet同期に失敗しました");
    } finally {
      setSyncLoading(false);
    }
  }, [load]);

  // ===== RMS認証情報 =====
  const verifyAndLoadRmsCredentials = useCallback(async () => {
    const { tenantId, companyId, shopUrl, busy } = rmsCredDlg;
    if (busy) return;

    const rowKey = keyOf(companyId, shopUrl);
    setProxyBusyKey(rowKey);
    setRmsCredDlg((p) => ({ ...p, busy: true, error: "" }));

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("ログインユーザーが取得できません");
      }

      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(currentUser, provider);

      await currentUser.getIdToken(true);

      // RMS と Yahoo RPA を両方取得する。どちらか一方でも取れれば表示する
      const base = `/api/super/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}/${encodeURIComponent(shopUrl)}`;
      let src: RmsCredentialResponse = {};
      let rmsErrMsg = "";
      try {
        const r = await apiPostJson(`${base}/reveal-rms-credentials`, {});
        src = (r?.credentials ?? r ?? {}) as RmsCredentialResponse;
      } catch (e: any) {
        rmsErrMsg = String(e?.message ?? "rms_fetch_failed");
      }

      let yahooSrc: { yahooRpaId?: string; yahooRpaPass?: string; accountKey?: string } = {};
      let yahooErrMsg = "";
      try {
        const r2 = await apiPostJson(`${base}/reveal-yahoo-rpa-credentials`, {});
        yahooSrc = ((r2 as any)?.credentials ?? {}) as { yahooRpaId?: string; yahooRpaPass?: string; accountKey?: string };
      } catch (e: any) {
        yahooErrMsg = String(e?.message ?? "yahoo_fetch_failed");
      }
      setYahooAccountKeyInput(String(yahooSrc.accountKey ?? ""));

      if (rmsErrMsg && yahooErrMsg) {
        throw new Error(rmsErrMsg.includes("rms_credentials_not_found") && yahooErrMsg.includes("not_found")
          ? "rms_credentials_not_found"
          : rmsErrMsg);
      }

      const credentials: RmsCredentialResponse = {
        rmsUser: String(src.rmsUser ?? ""),
        rmsPass: String(src.rmsPass ?? ""),
        rmsPass2: String(src.rmsPass2 ?? ""),
        rmsPass3: String(src.rmsPass3 ?? ""),
        rmsPass4: String(src.rmsPass4 ?? ""),
        rmsPass5: String(src.rmsPass5 ?? ""),
        csvUser: String(src.csvUser ?? ""),
        csvPass: String(src.csvPass ?? ""),
        yahooRpaId: String(yahooSrc.yahooRpaId ?? ""),
        yahooRpaPass: String(yahooSrc.yahooRpaPass ?? ""),
        yahooAccountKey: String(yahooSrc.accountKey ?? ""),
      };

      setRmsCredDlg((p) => ({
        ...p,
        busy: false,
        error: "",
        verified: true,
        currentPassword: "",
        credentials,
        visibleSecrets: createEmptyVisibleSecrets(),
        copiedKey: "",
      }));
    } catch (e: any) {
      const msg: string = e?.message ?? "";
      const friendlyError = msg.includes("rms_credentials_not_found")
        ? "RMS情報が設定されていません。"
        : msg || "Google再認証またはRMS認証情報の取得に失敗しました";
      setRmsCredDlg((p) => ({
        ...p,
        busy: false,
        error: friendlyError,
      }));
    } finally {
      setProxyBusyKey("");
    }
  }, [rmsCredDlg]);

  const copyRmsCredential = useCallback(
    async (field: "rmsUser" | "csvUser" | "yahooRpaId" | RmsSecretKey) => {
      const credentials = rmsCredDlg.credentials;
      if (!credentials) return;

      const value = String(credentials[field] ?? "");
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);

        if (copiedTimerRef.current) {
          window.clearTimeout(copiedTimerRef.current);
          copiedTimerRef.current = null;
        }

        setRmsCredDlg((p) => ({ ...p, copiedKey: field }));
        copiedTimerRef.current = window.setTimeout(() => {
          setRmsCredDlg((p) => ({ ...p, copiedKey: "" }));
          copiedTimerRef.current = null;
        }, 1200);
      } catch (e: any) {
        setRmsCredDlg((p) => ({
          ...p,
          error: e?.message ?? "コピーに失敗しました",
        }));
      }
    },
    [rmsCredDlg.credentials]
  );

  const revealSecretTemporarily = useCallback((field: RmsSecretKey) => {
    const prev = revealTimerRef.current.get(field);
    if (prev) window.clearTimeout(prev);

    setRmsCredDlg((p) => ({
      ...p,
      visibleSecrets: {
        ...p.visibleSecrets,
        [field]: true,
      },
    }));

    const t = window.setTimeout(() => {
      setRmsCredDlg((p) => ({
        ...p,
        visibleSecrets: {
          ...p.visibleSecrets,
          [field]: false,
        },
      }));
      revealTimerRef.current.delete(field);
    }, 10_000);

    revealTimerRef.current.set(field, t);
  }, []);

  // ===== Auto-save updateField =====
  const updateField = useCallback(
    (
      companyId: string,
      shopUrl: string,
      field: keyof CompanyRow,
      value: any,
      opts?: { immediate?: boolean; debounceMs?: number }
    ) => {
      const key = keyOf(companyId, shopUrl);

      const nextSeq = (changeSeqRef.current.get(key) ?? 0) + 1;
      changeSeqRef.current.set(key, nextSeq);

      const delay = opts?.immediate ? 0 : typeof opts?.debounceMs === "number" ? opts.debounceMs : 700;

      setCompanies((prev) =>
        prev.map((c) => {
          if (c.companyId === companyId && c.shopUrl === shopUrl) {
            const updated: CompanyRow = { ...c, [field]: value, _dirty: true, _saveError: "" };
            latestRowRef.current.set(key, updated);
            return updated;
          }
          return c;
        })
      );

      queueSaveInternal(companyId, shopUrl, delay);
    },
    [queueSaveInternal]
  );

  const toggleEnabledMall = useCallback(
    (companyId: string, shopUrl: string, mall: (typeof AVAILABLE_MALLS)[number], checked: boolean) => {
      const row = companies.find((c) => c.companyId === companyId && c.shopUrl === shopUrl);
      const current = normalizeEnabledMalls(row?.enabledMalls);
      const next = checked
        ? Array.from(new Set([...current, mall]))
        : current.filter((m) => m !== mall);

      updateField(companyId, shopUrl, "enabledMalls", normalizeEnabledMalls(next), { immediate: true });
    },
    [companies, updateField]
  );

  const openShop = (companyId: string, shopUrl: string, tenantId: string) => {
    localStorage.removeItem("demoMode");
    localStorage.setItem("selectedTenant", tenantId);
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("selectedCompanyId", companyId);
    sessionStorage.setItem("login_tenant", tenantId);

    localStorage.setItem("impCompanyId", companyId);
    localStorage.setItem("impTenantId", tenantId);
    localStorage.setItem("impShopUrl", shopUrl);

    const url =
      `/super/open-company/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}` +
      `?shopUrl=${encodeURIComponent(shopUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openMallPage = (companyId: string, shopUrl: string, tenantId: string, pagePath: string) => {
    localStorage.removeItem("demoMode");
    const url =
      `/super/open-company/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}` +
      `?shopUrl=${encodeURIComponent(shopUrl)}&redirectTo=${encodeURIComponent(pagePath)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const missingCount = useMemo(
    () => companies.filter((c) => isBlank(c.companyName) || isBlank(c.shopName)).length,
    [companies]
  );

  // RN-305: 上のカード・プルダウンの数・表は、テストのお店を除いた一覧から数える（「テスト店舗を表示」を入れたときだけ全部）
  const baseCompanies = useMemo(
    () => (showTest ? companies : companies.filter((c) => !isTestCompany(c))),
    [companies, showTest]
  );
  const testCompanyCount = useMemo(() => companies.filter((c) => isTestCompany(c)).length, [companies]);

  const contractTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of CONTRACT_TYPE_OPTIONS) counts[s] = 0;
    for (const c of baseCompanies) {
      const key = getContractTypeValue(c);
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [baseCompanies]);

  const contractStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of CONTRACT_STATUS_OPTIONS) counts[s] = 0;
    for (const c of baseCompanies) {
      const key = normalizeContractStatusValue(c.trialStatus);
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [baseCompanies]);

  const planCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of PLAN_STATUS) counts[s] = 0;
    for (const c of baseCompanies) {
      const key = (c.plan || "").trim();
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [baseCompanies]);

  // RN-280: 有料のお店のプランの内訳（大きな数字のカードの下に出す）
  const paidPlanCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of baseCompanies) {
      if (normalizeContractStatusValue(c.trialStatus) !== "有料") continue;
      const key = (c.plan || "").trim() || "未設定";
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [baseCompanies]);

  const errKindCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of baseCompanies) {
      const k = errKindOf(c);
      if (k) counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [baseCompanies]);

  const monitoredCount = useMemo(() => baseCompanies.filter((c) => isMonitored(c)).length, [baseCompanies]);

  const n = (m: Record<string, number>, key: string) => m[key] ?? 0;
  // RN-280: プルダウンの選択肢の値。決まった一覧のうち「未設定」以外＋件数のあるもの＋いま選んでいるもの（0件でも外せるように）、
  //   さらに一覧に無い値で実際にお店があるもの（「すべて選択する」で漏らさないように）
  const optionValues = (list: string[], counts: Record<string, number>, kind: SummaryFilterKind) => {
    const selected = summaryFilters.filter((f) => f.kind === kind).map((f) => f.value);
    const base = list.filter((v) => v || n(counts, v) > 0 || selected.includes(v));
    const extra = Object.keys(counts).filter((v) => !list.includes(v) && (n(counts, v) > 0 || selected.includes(v)));
    return [...base, ...extra];
  };
  const runFillMissingNames = useCallback(async () => {
    setFillMsg("");
    setError("");

    const targets = companies
      .filter((c) => isBlank(c.companyName) || isBlank(c.shopName))
      .map((c) => ({
        tenantId: c.tenantId,
        accountId: c.companyId,
        shopId: c.shopUrl,
        shopUrl: c.shopUrl,
      }));

    if (targets.length === 0) {
      setFillMsg("未入力のデータはありません（会社名・ショップ名ともに入力済み）");
      return;
    }

    setFillLoading(true);
    try {
      const r = await apiPostJson("/api/super/companies-fill-missing-names", { targets });
      const msg =
        `対象 ${r?.targetCount ?? targets.length} 件 / ` +
        `成功 ${r?.okCount ?? 0} 件 / ` +
        `失敗 ${r?.errorCount ?? 0} 件 / ` +
        `companyName更新 ${r?.updatedCompany ?? 0} / ` +
        `shopName更新 ${r?.updatedShop ?? 0}`;
      setFillMsg(msg);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "自動補完の実行に失敗しました");
    } finally {
      setFillLoading(false);
    }
  }, [companies, load]);

  const resolveDataFetchError = useCallback(
    async (tenantId: string, companyId: string, shopUrl: string) => {
      setError("");

      setCompanies((prev) =>
        prev.map((c) =>
          c.companyId === companyId && c.shopUrl === shopUrl ? { ...c, _resolvingErr: true, _resolveError: "" } : c
        )
      );

      try {
        await apiPostJson(`${apiPathForShop(tenantId, companyId, shopUrl)}/resolve-datafetch-error`, {});
        await load();
      } catch (e: any) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.companyId === companyId && c.shopUrl === shopUrl
              ? { ...c, _resolvingErr: false, _resolveError: e?.message ?? "対応済み更新に失敗しました" }
              : c
          )
        );
      } finally {
        setCompanies((prev) =>
          prev.map((c) => (c.companyId === companyId && c.shopUrl === shopUrl ? { ...c, _resolvingErr: false } : c))
        );
      }
    },
    [load]
  );

  const resolveRmsAuthError = useCallback(
    async (tenantId: string, companyId: string, shopUrl: string) => {
      setError("");

      setCompanies((prev) =>
        prev.map((c) =>
          c.companyId === companyId && c.shopUrl === shopUrl
            ? { ...c, _resolvingRmsAuth: true, _resolveRmsAuthError: "" }
            : c
        )
      );

      try {
        await apiPostJson(`${apiPathForShop(tenantId, companyId, shopUrl)}/resolve-rms-auth-error`, {});
        await load();
      } catch (e: any) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.companyId === companyId && c.shopUrl === shopUrl
              ? { ...c, _resolvingRmsAuth: false, _resolveRmsAuthError: e?.message ?? "RMS認証状態の解除に失敗しました" }
              : c
          )
        );
      } finally {
        setCompanies((prev) =>
          prev.map((c) =>
            c.companyId === companyId && c.shopUrl === shopUrl ? { ...c, _resolvingRmsAuth: false } : c
          )
        );
      }
    },
    [load]
  );

  // RN-285: 取得停止の経緯を開く（読むだけ）
  const openRmsHistory = useCallback(async (tenantId: string, companyId: string, shopUrl: string, companyName: string) => {
    setHistoryDlg({ open: true, companyId, shopUrl, companyName, loading: true, error: "", items: [] });
    try {
      const r = await apiGetJson<{ items: RmsHistoryItem[] }>(`${apiPathForShop(tenantId, companyId, shopUrl)}/history`);
      setHistoryDlg((p) =>
        p.open && p.companyId === companyId && p.shopUrl === shopUrl
          ? { ...p, loading: false, items: Array.isArray(r?.items) ? r.items.filter((it) => !isDeferredDryNotice(it)) : [] }
          : p
      );
    } catch (e: any) {
      setHistoryDlg((p) =>
        p.open && p.companyId === companyId && p.shopUrl === shopUrl
          ? { ...p, loading: false, error: e?.message ?? "経緯の読み込みに失敗しました" }
          : p
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeRmsHistory = useCallback(() => {
    setHistoryDlg((p) => ({ ...p, open: false }));
  }, []);

  const toggleSummaryFilter = useCallback((kind: SummaryFilterKind, value: string) => {
    setSummaryFilters((prev) => {
      const exists = prev.some((f) => f.kind === kind && f.value === value);

      if (exists) {
        return prev.filter((f) => !(f.kind === kind && f.value === value));
      }

      return [...prev, { kind, value }];
    });
  }, []);

  const clearSummaryFilter = useCallback(() => {
    setSummaryFilters([]);
  }, []);

  const isSummaryFilterActive = useCallback(
    (kind: SummaryFilterKind, value: string) => {
      return summaryFilters.some((f) => f.kind === kind && f.value === value);
    },
    [summaryFilters]
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const normalizeForSearch = (v: any) =>
    String(v ?? "")
      .normalize("NFKC")
      .toLowerCase();

  const splitSearchTerms = (v: any) =>
    normalizeForSearch(v)
      .split(/[\s\u3000,、，]+/)
      .map((x) => x.trim())
      .filter(Boolean);

  const getSortVal = (c: CompanyRow, key: SortKey): number | string => {
    switch (key) {
      case "registeredAt": {
        const ms = toMillis(c.registeredAt);
        return ms ?? -1;
      }
      case "dataFetchErrLastAt": {
        const ms = toMillis(c.dataFetchErrLastAt);
        return ms ?? -1;
      }
      case "dataFetch":
        return c.dataFetch ? 1 : 0;
      case "dataFetchErrCount":
        return typeof c.dataFetchErrCount === "number" ? c.dataFetchErrCount : -1;
      case "memberCount":
        return typeof c.memberCount === "number" ? c.memberCount : -1;
      case "contractType":
        return getContractTypeValue(c);
      case "trialStatus":
        return normalizeContractStatusValue(c.trialStatus);
      default:
        return String((c as any)[key] ?? "");
    }
  };

  const visibleCompanies = dedupeCompanies([...baseCompanies])
    .filter((c) => {
      const trialStatus = normalizeContractStatusValue(c.trialStatus);
      const contractType = getContractTypeValue(c);

      const terms = splitSearchTerms(search);
      const searchTargets = [
        normalizeForSearch(c.shopUrl),
        normalizeForSearch(c.companyName),
        normalizeForSearch(c.shopName),
      ];
      const hit =
        terms.length === 0 || terms.some((term) => searchTargets.some((target) => target.includes(term)));

      if (!hit) return false;

      if (summaryFilters.length > 0) {
        const selectedContractTypes = new Set(
          summaryFilters.filter((f) => f.kind === "contractType").map((f) => f.value)
        );
        const selectedTrialStatuses = new Set(
          summaryFilters.filter((f) => f.kind === "trialStatus").map((f) => f.value)
        );
        const selectedPlans = new Set(summaryFilters.filter((f) => f.kind === "plan").map((f) => f.value));

        // kind内はOR、kind間はAND
        if (selectedContractTypes.size > 0 && !selectedContractTypes.has(contractType)) return false;
        if (selectedTrialStatuses.size > 0 && !selectedTrialStatuses.has(trialStatus)) return false;
        if (selectedPlans.size > 0 && !selectedPlans.has((c.plan || "").trim())) return false;
      }

      if (monitoredOnly && !isMonitored(c)) return false;

      if (errKinds.length > 0) {
        const k = errKindOf(c);
        if (!k || !errKinds.includes(k)) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (!sortKey) return 0;
      const va = getSortVal(a, sortKey);
      const vb = getSortVal(b, sortKey);

      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "ja");

      return sortDir === "asc" ? cmp : -cmp;
    });

  const bulkMailTargets = useMemo<BulkMailTarget[]>(
    () =>
      visibleCompanies.map((c) => ({
        tenantId: c.tenantId,
        companyId: c.companyId,
        shopUrl: c.shopUrl,
        companyName: c.companyName || "",
        shopName: c.shopName || "",
        trialStatus: normalizeContractStatusValue(c.trialStatus),
        plan: (c.plan || "").trim(),
      })),
    [visibleCompanies]
  );

  // ダイアログ内絞り込み適用後の送信対象（チェックなし=全件、kind内はOR・kind間はAND）
  const filteredBulkMailTargets = useMemo<BulkMailTarget[]>(() => {
    const statusSet = new Set(bulkMailDlg.statusFilters);
    const planSet = new Set(bulkMailDlg.planFilters);
    return bulkMailDlg.targets.filter((t) => {
      if (statusSet.size > 0 && !statusSet.has(String(t.trialStatus ?? ""))) return false;
      if (planSet.size > 0 && !planSet.has(String(t.plan ?? ""))) return false;
      return true;
    });
  }, [bulkMailDlg.targets, bulkMailDlg.statusFilters, bulkMailDlg.planFilters]);

  const bulkMailCanceledCount = useMemo(
    () => filteredBulkMailTargets.filter((t) => t.trialStatus === "解約済").length,
    [filteredBulkMailTargets]
  );

  const bulkMailStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of bulkMailDlg.targets) {
      const key = String(t.trialStatus ?? "");
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [bulkMailDlg.targets]);

  const bulkMailPlanCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of bulkMailDlg.targets) {
      const key = String(t.plan ?? "");
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [bulkMailDlg.targets]);

  // 絞り込み変更時は、作成済み下書きと対象がズレないよう下書きを無効化する
  const toggleBulkMailFilter = useCallback((kind: "status" | "plan", value: string) => {
    setBulkMailDlg((prev) => {
      const key = kind === "status" ? "statusFilters" : "planFilters";
      const current = prev[key];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return {
        ...prev,
        [key]: next,
        draftJobId: "",
        previewRows: [],
        okCount: 0,
        skipCount: 0,
        recipientCount: 0,
        confirmCountInput: "",
        tested: false,
        error: "",
      };
    });
  }, []);

  const handleCsvDownload = () => {
    const rows = visibleCompanies.map((c) => {
        const errCount = typeof c.dataFetchErrCount === "number" ? c.dataFetchErrCount : 0;
        const unresolved = isErrActive(c);
        const rpp = rppStatusParts(c);

        return {
            tenantId: c.tenantId,
            shopUrl: c.shopUrl,
            registeredAt: toYmdHm(c.registeredAt) || toYmd(c.registeredAt) || "",
            startDate: c.startDate || "",
            endDate: c.endDate || "",
            companyName: c.companyName || "",
            shopName: c.shopName || "",
            contractType: getContractTypeValue(c),
            trialStatus: normalizeContractStatusValue(c.trialStatus),
            plan: c.plan || "",
            enabledMalls: normalizeEnabledMalls(c.enabledMalls).map((mall) => MALL_LABELS[mall as (typeof AVAILABLE_MALLS)[number]]).join(", "),
            dataFetch: c.dataFetch ? "ON" : "OFF",
            dataFetchErrStatus: errCount === 0 ? "" : unresolved ? "未解決" : "解決済み",
            dataFetchErrCount: errCount === 0 ? "" : String(errCount),
            dataFetchErrLastAt: toYmdHm(c.dataFetchErrLastAt) || "",
            dataFetchErrLastMsg: c.dataFetchErrLastMsg || c.dataFetchErrLastCode || "",
            rppUpdateFlag: c.rppUpdateFlag ? "ON" : "OFF",
            rppUpdateStatus:
              rpp.kind === "error" ? `⚠ ${rpp.at} ${rpp.msg}`.trim() : rpp.kind === "ok" ? `OK ${rpp.at}` : "",
            rppTaskSheetId: c.rppTaskSheetId || "",
            operationStatus: c.operationStatus || "",
            chatworkRoomId: c.chatworkRoomId || "",
            chatworkContactRoomId: c.chatworkContactRoomId || "",
            notifyStatus: c.notifyStatus || "",
            memberCount: c.memberCount != null ? String(c.memberCount) : "",
            note: c.note || "",
            companyId: c.companyId,
        };
    });
    const cols: CsvCol<(typeof rows)[0]>[] = [
        { key: "tenantId",              header: "テナント" },
        { key: "shopUrl",               header: "ショップURL" },
        { key: "registeredAt",          header: "登録日" },
        { key: "startDate",             header: "申込日" },
        { key: "endDate",               header: "終了日" },
        { key: "companyName",           header: "会社名" },
        { key: "shopName",              header: "ショップ名" },
        { key: "contractType",          header: "契約内容" },
        { key: "trialStatus",           header: "契約ステータス" },
        { key: "plan",                  header: "プラン" },
        { key: "enabledMalls",          header: "利用モール" },
        { key: "dataFetch",             header: "データ" },
        { key: "dataFetchErrStatus",    header: "エラー状態" },
        { key: "dataFetchErrCount",     header: "エラー件数" },
        { key: "dataFetchErrLastAt",    header: "最終エラー日時" },
        { key: "dataFetchErrLastMsg",   header: "エラーメッセージ" },
        { key: "rppUpdateFlag",         header: "RPP更新" },
        { key: "rppUpdateStatus",       header: "RPP更新エラー" },
        { key: "rppTaskSheetId",        header: "RPP設定シートID" },
        { key: "operationStatus",       header: "ステータス" },
        { key: "chatworkRoomId",        header: "チャットワーク通知用" },
        { key: "chatworkContactRoomId", header: "チャットワーク連絡用" },
        { key: "notifyStatus",          header: "通知" },
        { key: "memberCount",           header: "ユーザー" },
        { key: "note",                  header: "備考" },
        { key: "companyId",             header: "会社ID" },
    ];
    downloadCsv(`companies_${dayjs().format("YYYYMMDDHHmmss")}`, rows, cols);
  }

  const openBulkMailDialog = useCallback(() => {
    setError("");

    if (bulkMailTargets.length === 0) {
      setError("送信対象がありません（現在の表示結果が0件です）");
      return;
    }

    setBulkMailDlg((prev) => ({
      ...prev,
      open: true,
      error: "",
      loading: false,
      testSending: false,
      sending: false,
      draftJobId: "",
      targets: bulkMailTargets,
      statusFilters: [],
      planFilters: [],
      previewRows: [],
      okCount: 0,
      skipCount: 0,
      recipientCount: 0,
      confirmCountInput: "",
      tested: false,
    }));
  }, [bulkMailTargets]);

  const createBulkMailDraft = useCallback(async () => {
    if (bulkMailDlg.loading || bulkMailDlg.testSending || bulkMailDlg.sending) return;

    if (!bulkMailDlg.subject.trim()) {
      setBulkMailDlg((p) => ({ ...p, error: "件名を入力してください" }));
      return;
    }
    if (!bulkMailDlg.body.trim()) {
      setBulkMailDlg((p) => ({ ...p, error: "本文を入力してください" }));
      return;
    }
    if (!filteredBulkMailTargets.length) {
      setBulkMailDlg((p) => ({ ...p, error: "送信対象がありません（絞り込み条件をご確認ください）" }));
      return;
    }

    setBulkMailDlg((p) => ({
      ...p,
      loading: true,
      error: "",
      draftJobId: "",
      previewRows: [],
      okCount: 0,
      skipCount: 0,
      recipientCount: 0,
      confirmCountInput: "",
      tested: false,
    }));

    try {
      const r = await apiPostJson("/api/super/companies/bulk-email/draft", {
        // 件名を手で書き換えても識別タグが外れないよう、送信直前に必ず付与する
        subject: withBulkMailSubjectTag(bulkMailDlg.subject),
        body: bulkMailDlg.body,
        targets: filteredBulkMailTargets,
      });

      setBulkMailDlg((p) => ({
        ...p,
        loading: false,
        error: "",
        draftJobId: String(r?.jobId ?? ""),
        previewRows: Array.isArray(r?.rows) ? (r.rows as BulkMailPreviewRow[]) : [],
        okCount: Number(r?.okCount ?? 0),
        skipCount: Number(r?.skipCount ?? 0),
        recipientCount: Number(r?.recipientCount ?? 0),
        confirmCountInput: "",
        tested: false,
      }));
    } catch (e: any) {
      setBulkMailDlg((p) => ({
        ...p,
        loading: false,
        error: e?.message ?? "下書き作成に失敗しました",
      }));
    }
  }, [bulkMailDlg, filteredBulkMailTargets]);

  const sendBulkMailTest = useCallback(async () => {
    if (!bulkMailDlg.draftJobId) {
      setBulkMailDlg((p) => ({ ...p, error: "先に下書きを作成してください" }));
      return;
    }

    setBulkMailDlg((p) => ({ ...p, testSending: true, error: "" }));

    try {
      const r = await apiPostJson(
        `/api/super/companies/bulk-email/${encodeURIComponent(bulkMailDlg.draftJobId)}/test`,
        {}
      );

      setBulkMailDlg((p) => ({
        ...p,
        testSending: false,
        tested: true,
        error: "",
      }));

      setSyncMsg(`テスト送信完了: ${r?.sentTo ?? "-"} 宛 / サンプル ${r?.sampleShopUrl ?? "-"}`);
    } catch (e: any) {
      setBulkMailDlg((p) => ({
        ...p,
        testSending: false,
        error: e?.message ?? "テスト送信に失敗しました",
      }));
    }
  }, [bulkMailDlg.draftJobId]);

  const runBulkMailSend = useCallback(async () => {
    if (!bulkMailDlg.draftJobId) {
      setBulkMailDlg((p) => ({ ...p, error: "先に下書きを作成してください" }));
      return;
    }
    if (!bulkMailDlg.tested) {
      setBulkMailDlg((p) => ({ ...p, error: "先に自分宛てテスト送信を行ってください" }));
      return;
    }

    const confirmCount = Number(bulkMailDlg.confirmCountInput || 0);
    if (confirmCount !== bulkMailDlg.okCount) {
      setBulkMailDlg((p) => ({
        ...p,
        error: `確認用件数が一致しません。送信可能ショップ件数 ${bulkMailDlg.okCount} を入力してください`,
      }));
      return;
    }

    if (bulkMailCanceledCount > 0) {
      const proceed = window.confirm(
        `対象に「解約済」の店舗が ${bulkMailCanceledCount} 件含まれています。\nこのまま本送信しますか？`
      );
      if (!proceed) return;
    }

    setBulkMailDlg((p) => ({ ...p, sending: true, error: "" }));

    try {
      const r = await apiPostJson(
        `/api/super/companies/bulk-email/${encodeURIComponent(bulkMailDlg.draftJobId)}/send`,
        { confirmCount }
      );

      setSyncMsg(
        `一斉送信完了: ショップ ${r?.sentShopCount ?? 0} 件 / 受信者 ${r?.sentRecipientCount ?? 0} 件 / エラー ${r?.errorCount ?? 0} 件`
      );
      closeBulkMailDialog();
    } catch (e: any) {
      setBulkMailDlg((p) => ({
        ...p,
        sending: false,
        error: e?.message ?? "一斉送信に失敗しました",
      }));
    }
  }, [bulkMailDlg, bulkMailCanceledCount, closeBulkMailDialog]);

  // ===== Chatwork一斉投稿 =====
  const closeBulkChatworkDialog = useCallback(() => {
    setBulkChatworkDlg((prev) => ({
      ...createInitialBulkChatworkDialogState(),
      body: prev.body,
      senderId: prev.senderId,
      planOnly: prev.planOnly,
      testRoomId: prev.testRoomId,
    }));
  }, []);

  const invalidateBulkChatworkDraft = useCallback(
    (patch: Partial<Pick<BulkChatworkDialogState, "body" | "senderId" | "planOnly">>) => {
      setBulkChatworkDlg((prev) => ({
        ...prev,
        ...patch,
        draftJobId: "",
        previewRows: [],
        okCount: 0,
        skipCount: 0,
        confirmCountInput: "",
        tested: false,
        error: "",
      }));
    },
    []
  );

  const openBulkChatworkDialog = useCallback(async () => {
    setError("");

    if (bulkMailTargets.length === 0) {
      setError("対象がありません（現在の表示結果が0件です）");
      return;
    }

    setBulkChatworkDlg((prev) => ({
      ...prev,
      open: true,
      error: "",
      loading: false,
      testSending: false,
      sending: false,
      sendersLoading: true,
      draftJobId: "",
      targets: bulkMailTargets, // 対象は一斉メールと同じ「現在の表示結果」
      previewRows: [],
      okCount: 0,
      skipCount: 0,
      confirmCountInput: "",
      tested: false,
    }));

    try {
      const r = await apiGetJson<{ senders: ChatworkSenderOption[] }>("/api/super/chatwork-senders");
      const senders = Array.isArray(r?.senders) ? r.senders : [];
      setBulkChatworkDlg((p) => ({
        ...p,
        sendersLoading: false,
        senders,
        senderId: senders.some((x) => x.id === p.senderId) ? p.senderId : senders[0]?.id || "",
        error:
          senders.length === 0
            ? "投稿者が未登録です。先に「Chatwork投稿者」画面で担当者のトークンを登録してください"
            : p.error,
      }));
    } catch (e: any) {
      setBulkChatworkDlg((p) => ({
        ...p,
        sendersLoading: false,
        error: e?.message ?? "投稿者一覧の取得に失敗しました",
      }));
    }
  }, [bulkMailTargets]);

  const createBulkChatworkDraft = useCallback(async () => {
    if (bulkChatworkDlg.loading || bulkChatworkDlg.testSending || bulkChatworkDlg.sending) return;

    if (!bulkChatworkDlg.senderId) {
      setBulkChatworkDlg((p) => ({ ...p, error: "投稿者を選択してください" }));
      return;
    }
    if (!bulkChatworkDlg.body.trim()) {
      setBulkChatworkDlg((p) => ({ ...p, error: "本文を入力してください" }));
      return;
    }
    if (!bulkChatworkDlg.targets.length) {
      setBulkChatworkDlg((p) => ({ ...p, error: "対象がありません" }));
      return;
    }

    setBulkChatworkDlg((p) => ({
      ...p,
      loading: true,
      error: "",
      draftJobId: "",
      previewRows: [],
      okCount: 0,
      skipCount: 0,
      confirmCountInput: "",
      tested: false,
    }));

    try {
      const r = await apiPostJson("/api/super/companies/bulk-chatwork/draft", {
        senderId: bulkChatworkDlg.senderId,
        body: bulkChatworkDlg.body,
        planOnly: bulkChatworkDlg.planOnly,
        targets: bulkChatworkDlg.targets,
      });

      setBulkChatworkDlg((p) => ({
        ...p,
        loading: false,
        draftJobId: String(r?.jobId || ""),
        okCount: Number(r?.okCount || 0),
        skipCount: Number(r?.skipCount || 0),
        previewRows: Array.isArray(r?.rows) ? (r.rows as BulkChatworkPreviewRow[]) : [],
      }));
    } catch (e: any) {
      setBulkChatworkDlg((p) => ({
        ...p,
        loading: false,
        error: e?.message ?? "下書き作成に失敗しました",
      }));
    }
  }, [bulkChatworkDlg]);

  const sendBulkChatworkTest = useCallback(async () => {
    if (!bulkChatworkDlg.draftJobId) {
      setBulkChatworkDlg((p) => ({ ...p, error: "先に下書きを作成してください" }));
      return;
    }
    const testRoomId = bulkChatworkDlg.testRoomId.trim();
    if (!/^\d+$/.test(testRoomId)) {
      setBulkChatworkDlg((p) => ({ ...p, error: "テスト投稿先ルームIDを数字で入力してください" }));
      return;
    }

    setBulkChatworkDlg((p) => ({ ...p, testSending: true, error: "" }));

    try {
      await apiPostJson(
        `/api/super/companies/bulk-chatwork/${encodeURIComponent(bulkChatworkDlg.draftJobId)}/test`,
        { testRoomId }
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("bulkChatworkTestRoomId", testRoomId);
      }
      setBulkChatworkDlg((p) => ({ ...p, testSending: false, tested: true }));
    } catch (e: any) {
      setBulkChatworkDlg((p) => ({
        ...p,
        testSending: false,
        error: e?.message ?? "テスト投稿に失敗しました",
      }));
    }
  }, [bulkChatworkDlg.draftJobId, bulkChatworkDlg.testRoomId]);

  const runBulkChatworkSend = useCallback(async () => {
    if (!bulkChatworkDlg.draftJobId) {
      setBulkChatworkDlg((p) => ({ ...p, error: "先に下書きを作成してください" }));
      return;
    }
    if (!bulkChatworkDlg.tested) {
      setBulkChatworkDlg((p) => ({ ...p, error: "先にテスト投稿を行ってください" }));
      return;
    }

    const confirmCount = Number(bulkChatworkDlg.confirmCountInput || 0);
    if (confirmCount !== bulkChatworkDlg.okCount) {
      setBulkChatworkDlg((p) => ({
        ...p,
        error: `確認用件数が一致しません。投稿可能件数 ${bulkChatworkDlg.okCount} を入力してください`,
      }));
      return;
    }

    setBulkChatworkDlg((p) => ({ ...p, sending: true, error: "" }));

    try {
      const r = await apiPostJson(
        `/api/super/companies/bulk-chatwork/${encodeURIComponent(bulkChatworkDlg.draftJobId)}/send`,
        { confirmCount }
      );

      setSyncMsg(
        `Chatwork一斉投稿完了: 投稿 ${r?.sentCount ?? 0} 件 / エラー ${r?.errorCount ?? 0} 件`
      );
      closeBulkChatworkDialog();
    } catch (e: any) {
      setBulkChatworkDlg((p) => ({
        ...p,
        sending: false,
        error: e?.message ?? "一斉投稿に失敗しました",
      }));
    }
  }, [bulkChatworkDlg, closeBulkChatworkDialog]);

  const stickyDivider = "shadow-[inset_-1px_0_0_0_rgba(209,213,219,1)]";
  const rmsSecretRows = RMS_SECRET_KEYS.filter((k) => k !== "csvPass" && k !== "yahooRpaPass").filter(
    (k) => String(rmsCredDlg.credentials?.[k] ?? "").trim()
  );
  const csvSecretRows = RMS_SECRET_KEYS.filter((k) => k === "csvPass").filter(
    (k) => String(rmsCredDlg.credentials?.[k] ?? "").trim()
  );
  const yahooSecretRows = RMS_SECRET_KEYS.filter((k) => k === "yahooRpaPass").filter(
    (k) => String(rmsCredDlg.credentials?.[k] ?? "").trim()
  );
  const hasYahooRpaCreds = !!String(rmsCredDlg.credentials?.yahooRpaId ?? "").trim() || yahooSecretRows.length > 0;
  const hasYahooShop = !!String(rmsCredDlg.credentials?.yahooAccountKey ?? "").trim();

  const saveYahooAccountKey = async () => {
    const { tenantId, companyId, shopUrl } = rmsCredDlg;
    const accountKey = yahooAccountKeyInput.trim();
    if (!accountKey || yahooAccountSaving) return;
    setYahooAccountSaving(true);
    try {
      await apiPostJson(
        `/api/super/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}/${encodeURIComponent(shopUrl)}/yahoo-rpa-account`,
        { accountKey }
      );
      setRmsCredDlg((p) => ({
        ...p,
        credentials: p.credentials ? { ...p.credentials, yahooAccountKey: accountKey } : p.credentials,
      }));
      alert(`紐づけアカウントを ${accountKey} に変更しました。翌日の取得から反映されます`);
    } catch (e: any) {
      alert(`変更に失敗しました: ${e?.message ?? ""}`);
    } finally {
      setYahooAccountSaving(false);
    }
  };

  return (
    <div className="w-full max-w-none min-w-0 h-full flex flex-col gap-2 px-3 py-4">
      <div className="flex items-start justify-between gap-3" style={density === "compact" ? { zoom: 0.8 } : undefined}>
        {/* RN-280: 倍率で並びが変わらないよう、左側は段を固定する（1段目＝題名・検索・件数／2段目＝絞り込み／3段目＝カード／札） */}
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h1 className="text-lg font-bold">ショップ管理（SuperAdmin）</h1>

          <input
            type="text"
            placeholder="ショップURL / 企業名 / ショップ名で検索"
            className="border px-2 py-1 rounded w-72 text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* RN-280: 件数・エラー数の1行は消した（「表示中」の欄とエラーのプルダウンに同じ数が出る）。未入力の数は操作のメニューへ */}
          {loading && <div className="text-xs text-slate-600">読み込み中…</div>}

          </div>
          <div className="flex items-center gap-3 shrink-0 flex-wrap justify-end text-xs">
            <div className="inline-flex items-center rounded border overflow-hidden" title="表示の大きさ（この画面だけ）">
              <span className="px-2 text-slate-500">表示</span>
              {(["compact", "normal"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`px-2 py-1 whitespace-nowrap ${density === d ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                  onClick={() => setDensity(d)}
                >
                  {d === "compact" ? "コンパクト" : "標準"}
                </button>
              ))}
            </div>
          </div>
          </div>

          {/* RN-280: 4つのチェックを、意味ごとのプルダウンにまとめた（エラーは種類で選ぶ・いくつでも選べる） */}
          <div className="flex items-center gap-4 flex-wrap text-xs text-slate-700">
            {!loading && (
              <MultiSelectDropdown
                label="エラー"
                options={[
                  ...(["stopped", "login", "other"] as ErrKind[]).map((k) => ({
                    value: k,
                    label: ERR_KIND_LABELS[k],
                    count: n(errKindCounts, k),
                    group: "楽天",
                  })),
                  // Amazon・Yahoo! のエラーは、この一覧のデータにまだ入っていない（同期ヘルスの画面で見る）
                  { value: "__amazon", label: "準備中（同期ヘルスで見る）", group: "Amazon", disabled: true },
                  { value: "__yahoo", label: "準備中（同期ヘルスで見る）", group: "Yahoo!", disabled: true },
                ]}
                selected={errKinds}
                onToggle={(v) =>
                  setErrKinds((prev) => (prev.includes(v as ErrKind) ? prev.filter((x) => x !== v) : [...prev, v as ErrKind]))
                }
                onClear={() => setErrKinds([])}
                onSelectAll={() => setErrKinds(["stopped", "login", "other"])}
              />
            )}
            {!loading && (
              <>
                <MultiSelectDropdown
                  label="契約ステータス"
                  options={optionValues(CONTRACT_STATUS_OPTIONS, contractStatusCounts, "trialStatus").map((v) => ({
                    value: v,
                    label: v || "未設定",
                    count: n(contractStatusCounts, v),
                  }))}
                  selected={summaryFilters.filter((f) => f.kind === "trialStatus").map((f) => f.value)}
                  onToggle={(v) => toggleSummaryFilter("trialStatus", v)}
                  onClear={() => setSummaryFilters((prev) => prev.filter((f) => f.kind !== "trialStatus"))}
                  onSelectAll={() =>
                    setSummaryFilters((prev) => [
                      ...prev.filter((f) => f.kind !== "trialStatus"),
                      ...optionValues(CONTRACT_STATUS_OPTIONS, contractStatusCounts, "trialStatus").map((v) => ({ kind: "trialStatus" as const, value: v })),
                    ])
                  }
                />
                <MultiSelectDropdown
                  label="契約内容"
                  options={optionValues(CONTRACT_TYPE_OPTIONS, contractTypeCounts, "contractType").map((v) => ({
                    value: v,
                    label: v || "未設定",
                    count: n(contractTypeCounts, v),
                  }))}
                  selected={summaryFilters.filter((f) => f.kind === "contractType").map((f) => f.value)}
                  onToggle={(v) => toggleSummaryFilter("contractType", v)}
                  onClear={() => setSummaryFilters((prev) => prev.filter((f) => f.kind !== "contractType"))}
                  onSelectAll={() =>
                    setSummaryFilters((prev) => [
                      ...prev.filter((f) => f.kind !== "contractType"),
                      ...optionValues(CONTRACT_TYPE_OPTIONS, contractTypeCounts, "contractType").map((v) => ({ kind: "contractType" as const, value: v })),
                    ])
                  }
                />
                <MultiSelectDropdown
                  label="プラン"
                  options={optionValues(PLAN_STATUS, planCounts, "plan").map((v) => ({
                    value: v,
                    label: v || "未設定",
                    count: n(planCounts, v),
                  }))}
                  selected={summaryFilters.filter((f) => f.kind === "plan").map((f) => f.value)}
                  onToggle={(v) => toggleSummaryFilter("plan", v)}
                  onClear={() => setSummaryFilters((prev) => prev.filter((f) => f.kind !== "plan"))}
                  onSelectAll={() =>
                    setSummaryFilters((prev) => [
                      ...prev.filter((f) => f.kind !== "plan"),
                      ...optionValues(PLAN_STATUS, planCounts, "plan").map((v) => ({ kind: "plan" as const, value: v })),
                    ])
                  }
                />
              </>
            )}
            <div>
            <OperationsMenu
              targetCount={visibleCompanies.length}
              busyLabel={fillLoading ? "会社名・ショップ名を同期中…" : null}
              groups={[
                {
                  title: `書き出す（表示中 ${visibleCompanies.length} 件）`,
                  items: [{ label: "CSV出力", disabled: loading || visibleCompanies.length === 0, onClick: handleCsvDownload }],
                },
                {
                  title: `お客様へ送る（表示中 ${visibleCompanies.length} 件）`,
                  warn: true,
                  items: [
                    {
                      label: "一斉メール作成",
                      title: "現在の表示結果を対象に、ショップ管理者への一斉メール送信下書きを作成します",
                      disabled: loading || visibleCompanies.length === 0,
                      onClick: openBulkMailDialog,
                    },
                    {
                      label: "Chatwork一斉投稿",
                      title: "現在の表示結果を対象に、Chatwork連絡用ルームへの一斉投稿下書きを作成します（投稿者名義を選択可能）",
                      disabled: loading || visibleCompanies.length === 0,
                      onClick: openBulkChatworkDialog,
                    },
                  ],
                },
                {
                  title: "データを直す",
                  items: [
                    {
                      label: `会社名・ショップ名同期（未入力 ${missingCount} 件）`,
                      title: "楽天市場から会社名・ショップ名を取得して同期",
                      disabled: fillLoading || loading,
                      onClick: runFillMissingNames,
                    },
                  ],
                },
              ]}
            />
            </div>
          </div>

          {!loading && (
            <div className="flex flex-col gap-1 text-xs text-slate-700">
              {/* RN-280: 契約ステータスごとの店舗数を大きな数字で（10/8 MTG）。押すと一覧を絞り込む（もう一度押すと外す） */}
              <div className="flex items-stretch gap-2 flex-wrap">
                {(["有料", "トライアル", "解約予定", "パートナー", "無料", "アンバサダー"] as const)
                  .filter((st) => st !== "アンバサダー" || n(contractStatusCounts, st) > 0)
                  .map((st) => {
                    const active = isSummaryFilterActive("trialStatus", st);
                    const planLine =
                      st === "有料"
                        ? PLAN_STATUS.filter((pl) => pl && n(paidPlanCounts, pl) > 0)
                            .concat(n(paidPlanCounts, "未設定") > 0 ? ["未設定"] : [])
                            .map((pl) => `${pl.replace("プラン", "")} ${n(paidPlanCounts, pl)}`)
                            .join("・")
                        : "";
                    return (
                      <button
                        key={st}
                        type="button"
                        className={`flex flex-col items-start rounded-lg border bg-white px-3 py-2 text-left transition ${active ? "ring-2 ring-slate-900 border-slate-900" : "border-slate-200 hover:border-slate-400"
                          } ${st === "有料" ? "min-w-[150px]" : "min-w-[96px]"}`}
                        style={{ borderLeft: `6px solid ${st === "トライアル" ? "#94A3B8" : CONTRACT_STATUS_COLORS[st] || "#ccc"}` }}
                        onClick={() => toggleSummaryFilter("trialStatus", st)}
                        title={`${st}のお店だけを表示（もう一度押すと元に戻す）`}
                      >
                        <span className="text-xs text-slate-600">{st}</span>
                        <span className="text-2xl font-bold leading-tight text-slate-900">
                          {n(contractStatusCounts, st)}
                          <span className="ml-1 text-xs font-normal text-slate-500">店舗</span>
                        </span>
                        {planLine && <span className="text-[11px] text-slate-500">{planLine}</span>}
                      </button>
                    );
                  })}
                <div className="mx-1 w-px self-stretch bg-slate-200" />
                <button
                  type="button"
                  className={`flex flex-col items-start rounded-lg border bg-sky-50 px-3 py-2 text-left transition min-w-[150px] ${monitoredOnly ? "ring-2 ring-sky-700 border-sky-700" : "border-sky-300 hover:border-sky-500"}`}
                  style={{ borderLeft: "6px solid #0369A1" }}
                  onClick={() => setMonitoredOnly((v) => !v)}
                  title="同期ヘルスの「監視対象」と同じ決め方で数えています。データ取得が ON で、解約済・解約予定ではなく、無料のプランではなく、契約内容が「テスト」ではないお店。押すと監視対象のお店だけを表示（もう一度押すと元に戻す）"
                >
                  <span className="text-xs font-semibold text-sky-900">監視対象</span>
                  <span className="text-2xl font-bold leading-tight text-sky-900">
                    {monitoredCount}
                    <span className="ml-1 text-xs font-normal text-sky-800">店舗</span>
                  </span>
                </button>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
                {(["解約済", ""] as const).map((st) => (
                  <button
                    key={st || "未設定"}
                    type="button"
                    className={`underline-offset-2 hover:underline ${isSummaryFilterActive("trialStatus", st) ? "font-semibold text-slate-900 underline" : ""}`}
                    onClick={() => toggleSummaryFilter("trialStatus", st)}
                  >
                    {st || "契約ステータス未設定"} {n(contractStatusCounts, st)}店舗
                  </button>
                ))}
                {/* RN-305: テストのお店は既定で隠す（数にも入れない）。入れたときだけ表と数に入る */}
                <label
                  className="inline-flex items-center gap-1 cursor-pointer"
                  title="契約内容が「テスト」のお店は、ふだんは表にもカードの数にも入れていません。入れると表示します（ページを開き直すと隠れた状態に戻ります）"
                >
                  <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} />
                  テスト店舗を表示（{testCompanyCount}店舗）
                </label>
              </div>
              {/* RN-280: いま、どの条件で表示しているか（CSV 出力・一斉メールの対象もこの表示のまま） */}
              <div className="flex items-center gap-2 flex-wrap rounded border border-slate-200 bg-slate-50 px-2 py-1">
                <span className="font-semibold text-slate-800">表示中：</span>
                {(() => {
                  const conds: { key: string; label: string; clear: () => void }[] = [];
                  if (search.trim()) conds.push({ key: "search", label: `検索「${search.trim()}」`, clear: () => setSearch("") });
                  if (monitoredOnly) conds.push({ key: "monitored", label: "監視対象のお店だけ", clear: () => setMonitoredOnly(false) });
                  if (showTest) conds.push({ key: "test", label: "テスト店舗も表示", clear: () => setShowTest(false) });
                  if (errKinds.length > 0)
                    conds.push({ key: "err", label: `エラー：${errKinds.map((k) => ERR_KIND_LABELS[k]).join("・")}`, clear: () => setErrKinds([]) });
                  for (const kind of ["trialStatus", "contractType", "plan"] as const) {
                    const vals = summaryFilters.filter((f) => f.kind === kind).map((f) => f.value);
                    if (vals.length === 0) continue;
                    const name = kind === "trialStatus" ? "契約ステータス" : kind === "contractType" ? "契約内容" : "プラン";
                    conds.push({
                      key: kind,
                      label: `${name}：${vals.map((v) => v || "未設定").join("・")}`,
                      clear: () => setSummaryFilters((prev) => prev.filter((f) => f.kind !== kind)),
                    });
                  }
                  return (
                    <>
                      <span>
                        {conds.length === 0
                          ? `全 ${baseCompanies.length} 件（条件なし）`
                          : `${visibleCompanies.length} 件 ／ 全 ${baseCompanies.length} 件`}
                      </span>
                      {conds.map((c) => (
                        <span key={c.key} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-0.5">
                          {c.label}
                          <button type="button" className="text-slate-400 hover:text-slate-800" title="この条件を外す" onClick={c.clear}>
                            ×
                          </button>
                        </span>
                      ))}
                      {conds.length > 0 && (
                        <button
                          type="button"
                          className="text-slate-600 underline"
                          onClick={() => {
                            setSearch("");
                            setErrKinds([]);
                            setMonitoredOnly(false);
                            clearSummaryFilter();
                          }}
                        >
                          すべて解除
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      {syncMsg && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{syncMsg}</div>
      )}

      {fillMsg && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{fillMsg}</div>
      )}

      <div className="flex-1 min-h-0 overflow-auto w-full">
        <table
          className="w-max min-w-full table-fixed border-separate border-spacing-0"
          style={density === "compact" ? { zoom: 0.8 } : undefined}
        >
          <colgroup>
            {/* RN-280: 利用モールの列まで1画面に入るよう、前の列を詰めた（契約内容・契約ステータス・プランは同じ幅） */}
            {/* テナント：2列目の固定の位置 left-[6.5rem] と同じ幅にする */}
            <col style={{ width: "6.5rem" }} />
            <col style={{ width: "10rem" }} />
            <col style={{ width: "8rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "8.5rem" }} />
            <col style={{ width: "9rem" }} />
            <col style={{ width: "9rem" }} />
            <col style={{ width: "18rem" }} />
            <col style={{ width: "14rem" }} />
            <col style={{ width: "4.5rem" }} /> {/* RPP更新: データ取得チェックボックス列と同じ幅 */}
            <col style={{ width: "10rem" }} />
            <col style={{ width: "10rem" }} />
            <col style={{ width: "7rem" }} />
            <col style={{ width: "6rem" }} />
            <col />
            <col style={{ width: "12rem" }} />
            <col style={{ width: "6rem" }} />
            <col style={{ width: "7.5rem" }} />
            <col style={{ width: "6rem" }} />
          </colgroup>

          <thead className="font-semibold">
            <tr>
              <th
                className={`${thBase} cursor-pointer sticky left-0 z-40 border-r-0 ${stickyDivider} will-change-transform`}
                onClick={() => toggleSort("tenantId")}
              >
                テナント{sortKey === "tenantId" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th
                className={`${thBase} cursor-pointer sticky left-[6.5rem] z-40 border-r-0 ${stickyDivider} will-change-transform`}
                onClick={() => toggleSort("shopUrl")}
              >
                ショップURL{sortKey === "shopUrl" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("registeredAt")}>
                登録日{sortKey === "registeredAt" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={thBase}>申込日</th>
              <th className={thBase}>終了日</th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("companyName")}>
                会社名{sortKey === "companyName" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("shopName")}>
                ショップ名{sortKey === "shopName" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("contractType")}>
                契約内容{sortKey === "contractType" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("trialStatus")}>
                契約ステータス{sortKey === "trialStatus" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("plan")}>
                プラン{sortKey === "plan" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={thBase}>利用モール</th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("dataFetch")}>
                データ{sortKey === "dataFetch" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("dataFetchErrCount")}>
                自動取得エラー{sortKey === "dataFetchErrCount" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={thBase}>RMS認証</th>

              <th className={`${thBase} text-center`}>RPP更新</th>
              <th className={thBase}>RPP更新エラー</th>
              <th className={thBase}>RPP設定シートID</th>

              <th className={`${thBase} cursor-pointer`} onClick={() => toggleSort("operationStatus")}>
                ステータス{sortKey === "operationStatus" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={thBase}>チャットワーク通知用</th>
              <th className={thBase}>チャットワーク連絡用</th>
              <th className={thBase}>通知</th>

              <th className={`${thBase} cursor-pointer text-center`} onClick={() => toggleSort("memberCount")}>
                ユーザー{sortKey === "memberCount" && (sortDir === "asc" ? " ▲" : " ▼")}
              </th>

              <th className={thBase}>備考</th>
              <th className={thBase}>会社ID</th>
              <th className={thBase}>保存</th>
              <th className={thBase}>認証情報</th>
              <th className={thBase}>削除</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={27} className={`${tdBase} text-gray-500`}>
                  読み込み中...
                </td>
              </tr>
            )}

            {!loading && visibleCompanies.length === 0 && (
              <tr>
                <td colSpan={27} className={`${tdBase} text-gray-500`}>
                  該当する企業がありません
                </td>
              </tr>
            )}

            {visibleCompanies.map((c) => {
              const errCount = typeof c.dataFetchErrCount === "number" ? c.dataFetchErrCount : 0;
              const unresolved = isErrActive(c);
              const hasErr = errCount > 0;
              const hasLastOk = toMillis(c.dataFetchLastOkAt) != null;
              const rmsAuthStatus = String(c.rmsAuthStatus ?? "").trim().toLowerCase();
              const rmsAuthIsInvalid = rmsAuthStatus === "invalid";
              const rmsAuthIsValid = rmsAuthStatus === "valid";

              const rowKey = keyOf(c.companyId, c.shopUrl);
              const proxyBusy = proxyBusyKey === rowKey;

              return (
                <tr
                  key={`${c.tenantId}__${c.companyId}__${c.shopUrl}`}
                  className={`group hover:bg-gray-50 ${c._dirty ? "bg-yellow-50" : ""}`}
                >
                  <td
                    className={`${tdBase} sticky left-0 z-20 border-r-0 ${stickyDivider} will-change-transform text-left ${c._dirty ? "bg-yellow-50 group-hover:bg-yellow-50" : "bg-white group-hover:bg-gray-50"
                      }`}
                  >
                    {c.tenantId}
                  </td>

                  <td
                    className={`${tdBase} sticky left-[6.5rem] z-20 border-r-0 ${stickyDivider} will-change-transform ${c._dirty ? "bg-yellow-50 group-hover:bg-yellow-50" : "bg-white group-hover:bg-gray-50"
                      }`}
                  >
                    <button
                      onClick={() => openShop(c.companyId, c.shopUrl, c.tenantId)}
                      className="text-blue-600 hover:underline truncate block w-full text-left"
                      title={c.shopUrl}
                    >
                      {c.shopUrl}
                    </button>
                  </td>

                  <td className={tdBase} title={String(c.registeredAt ?? "")}>
                    {toYmdHm(c.registeredAt) || toYmd(c.registeredAt) || (c.registeredAt ? String(c.registeredAt) : "-")}
                  </td>

                  <td className={tdBase}>
                    <input
                      type="date"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      value={c.startDate || ""}
                      onChange={(e) => updateField(c.companyId, c.shopUrl, "startDate", e.target.value)}
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <input
                      type="date"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      value={c.endDate || ""}
                      onChange={(e) => updateField(c.companyId, c.shopUrl, "endDate", e.target.value)}
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <input
                      type="text"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      value={c.companyName || ""}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "companyName", e.target.value, { debounceMs: 500 })
                      }
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <input
                      type="text"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      value={c.shopName || ""}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "shopName", e.target.value, { debounceMs: 500 })
                      }
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <StatusSelect
                      value={getContractTypeValue(c)}
                      options={CONTRACT_TYPE_OPTIONS}
                      colors={CONTRACT_TYPE_COLORS}
                      onChange={(v) => updateField(c.companyId, c.shopUrl, "contractType", v, { immediate: true })}
                    />
                  </td>

                  <td className={tdBase}>
                    <StatusSelect
                      value={normalizeContractStatusValue(c.trialStatus)}
                      options={CONTRACT_STATUS_OPTIONS}
                      colors={CONTRACT_STATUS_COLORS}
                      onChange={(v) => updateField(c.companyId, c.shopUrl, "trialStatus", v, { immediate: true })}
                    />
                  </td>

                  <td className={tdBase}>
                    <StatusSelect
                      value={c.plan || ""}
                      options={PLAN_STATUS}
                      colors={PLAN_COLORS}
                      onChange={(v) => updateField(c.companyId, c.shopUrl, "plan", v, { immediate: true })}
                    />
                  </td>

                  <td className={tdBase}>
                    <div className="flex min-w-[7rem] flex-col gap-1">
                      {AVAILABLE_MALLS.map((mall) => {
                        const checked = normalizeEnabledMalls(c.enabledMalls).includes(mall);
                        return (
                          <label key={mall} className="inline-flex items-center gap-2 text-[11px] text-slate-700">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => toggleEnabledMall(c.companyId, c.shopUrl, mall, e.target.checked)}
                            />
                            <span>{MALL_LABELS[mall]}</span>
                          </label>
                        );
                      })}
                      {normalizeEnabledMalls(c.enabledMalls).includes("amazon") && (
                        <button
                          type="button"
                          className="mt-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
                          onClick={() => openMallPage(c.companyId, c.shopUrl, c.tenantId, "amazonSettings")}
                        >
                          Amazon設定
                        </button>
                      )}
                      {normalizeEnabledMalls(c.enabledMalls).includes("yahoo") && (
                        <button
                          type="button"
                          className="mt-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100"
                          onClick={() => openMallPage(c.companyId, c.shopUrl, c.tenantId, "yahooSettings")}
                        >
                          Yahoo設定
                        </button>
                      )}
                    </div>
                  </td>

                  <td className={`${tdBase} text-center`}>
                    <input
                      type="checkbox"
                      checked={!!c.dataFetch}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "dataFetch", e.target.checked, { immediate: true })
                      }
                    />
                  </td>

                  <td className={tdBase}>
                    {hasErr ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${unresolved ? "bg-red-100 text-red-700" : "bg-amber-50 text-amber-700"
                              }`}
                            title={unresolved ? "未解決（最新エラーが対応日時より新しい）" : "エラー履歴あり（対応済みの可能性）"}
                          >
                            ERR {errCount}
                          </span>
                          <span className="text-[11px] text-slate-600" title={String(c.dataFetchErrLastAt ?? "")}>
                            {toYmdHm(c.dataFetchErrLastAt) || "-"}
                          </span>
                        </div>

                        <div className="text-[11px] text-slate-700" title={c.dataFetchErrLastMsg || ""}>
                          {shortText(c.dataFetchErrLastMsg || c.dataFetchErrLastCode || "", 80) || "-"}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            className="border px-2 py-0.5 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                            disabled={!!c._resolvingErr}
                            onClick={() => resolveDataFetchError(c.tenantId, c.companyId, c.shopUrl)}
                            title="このショップの自動取得エラーを『対応済み』としてクリアします（同一shopUrlの重複もまとめてクリア）。RMS認証が INVALID のときは取得は止まったままです（先に「解除」が必要）"
                          >
                            {c._resolvingErr ? "更新中..." : "対応済み"}
                          </button>

                          {c._resolveError && (
                            <span className="text-[11px] text-red-600" title={c._resolveError}>
                              失敗
                            </span>
                          )}
                        </div>
                      </div>
                    ) : hasLastOk ? (
                      <div className="text-[11px] text-slate-600">
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                            OK
                          </span>
                          <span title={String(c.dataFetchLastOkAt ?? "")}>{toYmdHm(c.dataFetchLastOkAt) || "-"}</span>
                        </div>
                        {c.dataFetchErrResolvedAt && (
                          <div className="mt-1 text-[11px] text-slate-500" title={String(c.dataFetchErrResolvedAt ?? "")}>
                            直近対応: {toYmdHm(c.dataFetchErrResolvedAt)}{" "}
                            {c.dataFetchErrResolvedBy ? `(${c.dataFetchErrResolvedBy})` : ""}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-600">
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600">
                            未実行
                          </span>
                          <span>-</span>
                        </div>
                        {c.dataFetchErrResolvedAt && (
                          <div className="mt-1 text-[11px] text-slate-500" title={String(c.dataFetchErrResolvedAt ?? "")}>
                            直近対応: {toYmdHm(c.dataFetchErrResolvedAt)}{" "}
                            {c.dataFetchErrResolvedBy ? `(${c.dataFetchErrResolvedBy})` : ""}
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  <td className={tdBase}>
                    <div className="flex flex-col gap-1">
                      <div className="inline-flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                            rmsAuthIsInvalid
                              ? "bg-red-100 text-red-700"
                              : rmsAuthIsValid
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {rmsAuthIsInvalid ? "INVALID" : rmsAuthIsValid ? "VALID" : "未判定"}
                        </span>
                        <span className="text-[11px] text-slate-600" title={String(c.rmsAuthStatusUpdatedAt ?? "")}> 
                          {toYmdHm(c.rmsAuthStatusUpdatedAt) || "-"}
                        </span>
                      </div>

                      <div className="text-[11px] text-slate-700" title={c.rmsAuthErrorMessage || ""}>
                        {shortText(c.rmsAuthErrorMessage || "", 80) || "-"}
                      </div>

                      {(rmsAuthIsInvalid || normalizeEnabledMalls(c.enabledMalls).includes("rakuten")) && (
                        <div className="flex items-center gap-2">
                          {rmsAuthIsInvalid && (
                            <button
                              className="border px-2 py-0.5 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                              disabled={!!c._resolvingRmsAuth}
                              onClick={() => resolveRmsAuthError(c.tenantId, c.companyId, c.shopUrl)}
                              title="RMS認証のinvalid状態を解除し、次の取得でもう一度ログインを試します。取り直しが成功するまで、お客様の画面の警告は残ります（認証情報が直ったことを確かめてから押す）"
                            >
                              {c._resolvingRmsAuth ? "解除中..." : "解除"}
                            </button>
                          )}

                          {/* RN-285: 取得停止の経緯（読むだけ）。同じ欄の「解除」と同じ形のボタンにそろえる */}
                          {normalizeEnabledMalls(c.enabledMalls).includes("rakuten") && (
                            <button
                              type="button"
                              className="border px-2 py-0.5 rounded text-[11px] bg-white hover:bg-gray-50"
                              onClick={() => openRmsHistory(c.tenantId, c.companyId, c.shopUrl, c.companyName || "")}
                              title="止まった・知らせ・保存・対応済み・解除・再開の記録を見ます（読むだけ）"
                            >
                              経緯
                            </button>
                          )}

                          {c._resolveRmsAuthError && (
                            <span className="text-[11px] text-red-600" title={c._resolveRmsAuthError}>
                              失敗
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className={`${tdBase} text-center`}>
                    <input
                      type="checkbox"
                      checked={!!c.rppUpdateFlag}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "rppUpdateFlag", e.target.checked, { immediate: true })
                      }
                    />
                  </td>

                  <td className={tdBase}>
                    {(() => {
                      const s = rppStatusParts(c);
                      if (s.kind === "error")
                        return (
                          <span className="text-red-600 text-xs" title={c.rppUpdateErrLastMsg || ""}>
                            ⚠ {s.at}
                            {s.msg ? ` ${s.msg.slice(0, 30)}` : ""}
                          </span>
                        );
                      if (s.kind === "ok")
                        return <span className="text-gray-500 text-xs">OK {s.at}</span>;
                      return <span className="text-gray-400 text-xs">—</span>;
                    })()}
                  </td>

                  <td className={tdBase}>
                    <input
                      type="text"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      placeholder="設定シートのID"
                      value={c.rppTaskSheetId || ""}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "rppTaskSheetId", e.target.value, {
                          debounceMs: 500,
                        })
                      }
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <StatusSelect
                      value={c.operationStatus || ""}
                      options={OPERATION_STATUS}
                      colors={OPERATION_COLORS}
                      onChange={(v) => updateField(c.companyId, c.shopUrl, "operationStatus", v, { immediate: true })}
                    />
                  </td>

                  <td className={tdBase}>
                    <input
                      type="text"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      placeholder="例: 123456789"
                      value={c.chatworkRoomId || ""}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "chatworkRoomId", e.target.value, { debounceMs: 500 })
                      }
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <input
                      type="text"
                      className="border px-1.5 py-0.5 rounded w-full text-xs"
                      placeholder="例: 123456789"
                      value={c.chatworkContactRoomId || ""}
                      onChange={(e) =>
                        updateField(c.companyId, c.shopUrl, "chatworkContactRoomId", e.target.value, {
                          debounceMs: 500,
                        })
                      }
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={tdBase}>
                    <StatusSelect
                      value={c.notifyStatus || ""}
                      options={NOTICE_STATUS}
                      colors={NOTICE_COLORS}
                      onChange={(v) => updateField(c.companyId, c.shopUrl, "notifyStatus", v, { immediate: true })}
                    />
                  </td>

                  <td className={`${tdBase} text-center font-semibold`}>
                    {typeof c.memberCount === "number" ? `${c.memberCount} 人` : "-"}
                  </td>

                  <td className={tdBase}>
                    <textarea
                      className="border px-1.5 py-0.5 rounded w-full resize-y text-xs"
                      rows={2}
                      value={c.note || ""}
                      onChange={(e) => updateField(c.companyId, c.shopUrl, "note", e.target.value, { debounceMs: 900 })}
                      onBlur={() => flushSave(c.companyId, c.shopUrl)}
                    />
                  </td>

                  <td className={`${tdBase} text-center`}>{c.companyId}</td>

                  <td className={`${tdBase} text-center text-[11px]`}>
                    {c._saving ? (
                      <span className="text-slate-500">保存中...</span>
                    ) : c._saveError ? (
                      <button
                        className="text-red-600 hover:underline"
                        title={c._saveError}
                        onClick={() => flushSave(c.companyId, c.shopUrl)}
                      >
                        再保存
                      </button>
                    ) : c._dirty ? (
                      <span className="text-amber-600">未保存</span>
                    ) : (
                      <span className="text-slate-400">保存済</span>
                    )}
                  </td>

                  <td className={`${tdBase} text-center`}>
                    <button
                      className="border px-2 py-0.5 rounded text-[11px] bg-white hover:bg-gray-50 text-slate-800 disabled:opacity-50"
                      onClick={() => openRmsCredentialDialog(c.companyId, c.shopUrl, c.tenantId)}
                      disabled={proxyBusy || !!c._saving || !!c._resolvingErr || !!c._resolvingRmsAuth}
                      title="再認証後に認証情報（RMS/CSV/Yahoo RPAのID・パスワード）を表示します"
                    >
                      {proxyBusy ? "取得中..." : "認証情報"}
                    </button>
                  </td>

                  <td className={`${tdBase} text-center`}>
                    <button
                      className="border px-2 py-0.5 rounded text-[11px] bg-white hover:bg-red-50 text-red-600 disabled:opacity-50"
                      onClick={() => openDelete(c.companyId, c.shopUrl, c.tenantId)}
                      disabled={!!c._saving || !!c._resolvingErr || !!c._resolvingRmsAuth}
                      title="安全のため、削除確認でショップURLの入力が必要です"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {bulkMailDlg.open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeBulkMailDialog} />
          <div className="relative w-[980px] max-w-[96vw] max-h-[92vh] overflow-hidden rounded-lg bg-white shadow-lg border p-4 flex flex-col">
            <div className="text-sm font-semibold text-slate-900">ショップ管理者への一斉メール</div>

            <div className="mt-2 text-xs text-slate-600">
              手順: ①件名/本文を確認 → ②下書き作成 → ③自分宛てテスト送信 → ④送信可能件数を入力して本送信
            </div>

            {bulkMailDlg.error && (
              <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {bulkMailDlg.error}
              </div>
            )}

            <div className="mt-4">
              <div className="text-xs font-semibold mb-1">件名</div>
              <input
                type="text"
                className="border px-2 py-1 rounded w-full text-sm"
                value={bulkMailDlg.subject}
                onChange={(e) => invalidateBulkMailDraft({ subject: e.target.value })}
              />
              <div className="mt-1 text-[11px] text-slate-500">
                ※ 利益Naviからのメールと判別できるよう、件名の先頭に
                <span className="font-semibold">{BULK_MAIL_SUBJECT_TAG}</span>
                が自動で付きます（すでに付いている場合は重複しません）。
              </div>
            </div>

            <div className="mt-3">
              <div className="text-xs font-semibold mb-1">本文</div>
              <textarea
                className="border px-2 py-2 rounded w-full text-sm resize-y"
                rows={8}
                value={bulkMailDlg.body}
                onChange={(e) => invalidateBulkMailDraft({ body: e.target.value })}
              />
              <div className="mt-2 rounded border bg-slate-50 p-2">
                <div className="text-[11px] font-semibold text-slate-700">使用可能変数一覧</div>
                <div className="mt-1 grid grid-cols-1 gap-1 text-[11px] text-slate-600 sm:grid-cols-2">
                  <div>{"{{recipientName}}"} : 宛先ユーザーの表示名</div>
                  <div>{"{{shopName}}"} : ショップ名</div>
                  <div>{"{{companyName}}"} : 会社名</div>
                  <div>{"{{shopUrl}}"} : ショップURL</div>
                </div>
              </div>
            </div>

            <div className="mt-3 rounded border bg-slate-50 p-2 text-xs">
              <div className="font-semibold text-slate-700">対象の絞り込み（チェックなし＝表示中の全件）</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-slate-500 shrink-0">契約ステータス:</span>
                {CONTRACT_STATUS_OPTIONS.map((opt) => (
                  <label key={`bm-st-${opt || "未設定"}`} className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkMailDlg.statusFilters.includes(opt)}
                      onChange={() => toggleBulkMailFilter("status", opt)}
                    />
                    {opt || "未設定"}（{bulkMailStatusCounts[opt] ?? 0}）
                  </label>
                ))}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-slate-500 shrink-0">プラン:</span>
                {PLAN_STATUS.map((opt) => (
                  <label key={`bm-pl-${opt || "未設定"}`} className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkMailDlg.planFilters.includes(opt)}
                      onChange={() => toggleBulkMailFilter("plan", opt)}
                    />
                    {opt || "未設定"}（{bulkMailPlanCounts[opt] ?? 0}）
                  </label>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                ※ 絞り込みを変更すると下書きは無効になります（下書き作成からやり直してください）
              </div>
            </div>

            {bulkMailCanceledCount > 0 && (
              <div className="mt-3 text-xs font-semibold text-red-700 bg-red-50 border border-red-300 rounded p-2">
                ⚠ 対象に「解約済」の店舗が {bulkMailCanceledCount} 件含まれています。契約ステータスの絞り込みをご確認ください。
              </div>
            )}

            <div className="mt-4 grid grid-cols-4 gap-3 text-xs">
              <div className="rounded border bg-slate-50 p-2">
                対象ショップ: <b>{filteredBulkMailTargets.length}</b> 件
              </div>
              <div className="rounded border bg-emerald-50 p-2">
                送信可能: <b>{bulkMailDlg.okCount}</b> 件
              </div>
              <div className="rounded border bg-amber-50 p-2">
                除外: <b>{bulkMailDlg.skipCount}</b> 件
              </div>
              <div className="rounded border bg-blue-50 p-2">
                受信者合計: <b>{bulkMailDlg.recipientCount}</b> 件
              </div>
            </div>

            <div className="mt-4 min-h-0 flex-1 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-100">
                  <tr>
                    <th className="border px-2 py-1 text-left">ショップURL</th>
                    <th className="border px-2 py-1 text-left">会社名</th>
                    <th className="border px-2 py-1 text-left">ショップ名</th>
                    <th className="border px-2 py-1 text-left">送信先</th>
                    <th className="border px-2 py-1 text-left">状態</th>
                  </tr>
                </thead>
                <tbody>
                  {!bulkMailDlg.previewRows.length ? (
                    <tr>
                      <td colSpan={5} className="border px-2 py-3 text-slate-500">
                        まだ下書き未作成です
                      </td>
                    </tr>
                  ) : (
                    bulkMailDlg.previewRows.map((r) => (
                      <tr key={`${r.tenantId}__${r.companyId}__${r.shopUrl}`}>
                        <td className="border px-2 py-1">{r.shopUrl}</td>
                        <td className="border px-2 py-1">{r.companyName || "-"}</td>
                        <td className="border px-2 py-1">{r.shopName || "-"}</td>
                        <td className="border px-2 py-1 whitespace-pre-line break-all">{formatBulkMailRecipients(r)}</td>
                        <td className="border px-2 py-1">
                          {r.status === "ok" ? (
                            <span className="text-emerald-700 font-semibold">送信可</span>
                          ) : (
                            <span className="text-amber-700">{r.reason || "除外"}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs">
                <span>本送信するには送信可能件数</span>
                <b>{bulkMailDlg.okCount}</b>
                <span>を入力:</span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-20 text-sm"
                  value={bulkMailDlg.confirmCountInput}
                  onChange={(e) => setBulkMailDlg((p) => ({ ...p, confirmCountInput: e.target.value }))}
                  disabled={!bulkMailDlg.draftJobId || !bulkMailDlg.tested || bulkMailDlg.sending}
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                  onClick={closeBulkMailDialog}
                  disabled={bulkMailDlg.loading || bulkMailDlg.testSending || bulkMailDlg.sending}
                >
                  キャンセル
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-slate-700 text-white disabled:opacity-50"
                  onClick={createBulkMailDraft}
                  disabled={bulkMailDlg.loading || bulkMailDlg.testSending || bulkMailDlg.sending}
                >
                  {bulkMailDlg.loading ? "作成中..." : "下書き作成"}
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-blue-700 text-white disabled:opacity-50"
                  onClick={sendBulkMailTest}
                  disabled={!bulkMailDlg.draftJobId || bulkMailDlg.loading || bulkMailDlg.testSending || bulkMailDlg.sending}
                >
                  {bulkMailDlg.testSending ? "テスト送信中..." : "自分宛てテスト送信"}
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-red-600 text-white disabled:opacity-50"
                  onClick={runBulkMailSend}
                  disabled={
                    !bulkMailDlg.draftJobId ||
                    !bulkMailDlg.tested ||
                    bulkMailDlg.loading ||
                    bulkMailDlg.testSending ||
                    bulkMailDlg.sending ||
                    Number(bulkMailDlg.confirmCountInput || 0) !== bulkMailDlg.okCount
                  }
                >
                  {bulkMailDlg.sending ? "本送信中..." : "本送信"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkChatworkDlg.open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeBulkChatworkDialog} />
          <div className="relative w-[980px] max-w-[96vw] max-h-[92vh] overflow-hidden rounded-lg bg-white shadow-lg border p-4 flex flex-col">
            <div className="text-sm font-semibold text-slate-900">
              Chatwork一斉投稿（連絡用ルームへ担当者名義で投稿）
            </div>

            <div className="mt-2 text-xs text-slate-600">
              手順: ①投稿者・対象・本文を確認 → ②下書き作成 → ③テストルームへテスト投稿 → ④投稿可能件数を入力して本投稿
            </div>

            {bulkChatworkDlg.error && (
              <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {bulkChatworkDlg.error}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <label className="text-xs">
                <span className="font-semibold mr-2">投稿者</span>
                <select
                  className="border px-2 py-1 rounded text-sm"
                  value={bulkChatworkDlg.senderId}
                  onChange={(e) => invalidateBulkChatworkDraft({ senderId: e.target.value })}
                  disabled={
                    bulkChatworkDlg.sendersLoading ||
                    bulkChatworkDlg.loading ||
                    bulkChatworkDlg.testSending ||
                    bulkChatworkDlg.sending
                  }
                >
                  {bulkChatworkDlg.senders.length === 0 && (
                    <option value="">{bulkChatworkDlg.sendersLoading ? "読込中..." : "（未登録）"}</option>
                  )}
                  {bulkChatworkDlg.senders.map((sd) => (
                    <option key={sd.id} value={sd.id}>
                      {sd.name}
                      {sd.chatworkAccountName ? `（Chatwork: ${sd.chatworkAccountName}）` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-1 text-xs select-none">
                <input
                  type="checkbox"
                  checked={bulkChatworkDlg.planOnly}
                  onChange={(e) => invalidateBulkChatworkDraft({ planOnly: e.target.checked })}
                  disabled={bulkChatworkDlg.loading || bulkChatworkDlg.testSending || bulkChatworkDlg.sending}
                />
                スタンダード相当プランのみに投稿する
              </label>
            </div>

            <div className="mt-3">
              <div className="text-xs font-semibold mb-1">本文</div>
              <textarea
                className="border px-2 py-2 rounded w-full text-sm resize-y"
                rows={8}
                value={bulkChatworkDlg.body}
                onChange={(e) => invalidateBulkChatworkDraft({ body: e.target.value })}
              />
              <div className="mt-2 rounded border bg-slate-50 p-2">
                <div className="text-[11px] font-semibold text-slate-700">使用可能変数一覧</div>
                <div className="mt-1 grid grid-cols-1 gap-1 text-[11px] text-slate-600 sm:grid-cols-3">
                  <div>{"{{shopName}}"} : ショップ名</div>
                  <div>{"{{companyName}}"} : 会社名</div>
                  <div>{"{{shopUrl}}"} : ショップURL</div>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  ※ Chatworkの装飾タグ（[info][title][toall] など）もそのまま使えます。
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div className="rounded border bg-slate-50 p-2">
                対象ショップ: <b>{bulkChatworkDlg.targets.length}</b> 件
              </div>
              <div className="rounded border bg-emerald-50 p-2">
                投稿可能: <b>{bulkChatworkDlg.okCount}</b> 件
              </div>
              <div className="rounded border bg-amber-50 p-2">
                除外: <b>{bulkChatworkDlg.skipCount}</b> 件
              </div>
            </div>

            <div className="mt-4 min-h-0 flex-1 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-100">
                  <tr>
                    <th className="border px-2 py-1 text-left">ショップURL</th>
                    <th className="border px-2 py-1 text-left">会社名</th>
                    <th className="border px-2 py-1 text-left">ショップ名</th>
                    <th className="border px-2 py-1 text-left">プラン</th>
                    <th className="border px-2 py-1 text-left">連絡用ルームID</th>
                    <th className="border px-2 py-1 text-left">状態</th>
                  </tr>
                </thead>
                <tbody>
                  {!bulkChatworkDlg.previewRows.length ? (
                    <tr>
                      <td colSpan={6} className="border px-2 py-3 text-slate-500">
                        まだ下書き未作成です
                      </td>
                    </tr>
                  ) : (
                    bulkChatworkDlg.previewRows.map((r) => (
                      <tr key={`${r.tenantId}__${r.companyId}__${r.shopUrl}`}>
                        <td className="border px-2 py-1">{r.shopUrl}</td>
                        <td className="border px-2 py-1">{r.companyName || "-"}</td>
                        <td className="border px-2 py-1">{r.shopName || "-"}</td>
                        <td className="border px-2 py-1">{r.plan || "-"}</td>
                        <td className="border px-2 py-1">{r.roomId || "-"}</td>
                        <td className="border px-2 py-1">
                          {r.status === "ok" ? (
                            <span className="text-emerald-700 font-semibold">投稿可</span>
                          ) : (
                            <span className="text-amber-700">{r.reason || "除外"}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 text-xs flex-wrap">
                <label className="flex items-center gap-1">
                  <span>テスト投稿先ルームID:</span>
                  <input
                    type="text"
                    className="border px-2 py-1 rounded w-32 text-sm"
                    placeholder="数字のみ"
                    value={bulkChatworkDlg.testRoomId}
                    onChange={(e) =>
                      setBulkChatworkDlg((p) => ({ ...p, testRoomId: e.target.value }))
                    }
                    disabled={bulkChatworkDlg.testSending || bulkChatworkDlg.sending}
                  />
                </label>
                <div className="flex items-center gap-2">
                  <span>本投稿するには投稿可能件数</span>
                  <b>{bulkChatworkDlg.okCount}</b>
                  <span>を入力:</span>
                  <input
                    type="text"
                    className="border px-2 py-1 rounded w-20 text-sm"
                    value={bulkChatworkDlg.confirmCountInput}
                    onChange={(e) =>
                      setBulkChatworkDlg((p) => ({ ...p, confirmCountInput: e.target.value }))
                    }
                    disabled={!bulkChatworkDlg.draftJobId || !bulkChatworkDlg.tested || bulkChatworkDlg.sending}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                  onClick={closeBulkChatworkDialog}
                  disabled={bulkChatworkDlg.loading || bulkChatworkDlg.testSending || bulkChatworkDlg.sending}
                >
                  キャンセル
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-slate-700 text-white disabled:opacity-50"
                  onClick={createBulkChatworkDraft}
                  disabled={
                    bulkChatworkDlg.loading ||
                    bulkChatworkDlg.testSending ||
                    bulkChatworkDlg.sending ||
                    !bulkChatworkDlg.senderId
                  }
                >
                  {bulkChatworkDlg.loading ? "作成中..." : "下書き作成"}
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-blue-700 text-white disabled:opacity-50"
                  onClick={sendBulkChatworkTest}
                  disabled={
                    !bulkChatworkDlg.draftJobId ||
                    bulkChatworkDlg.loading ||
                    bulkChatworkDlg.testSending ||
                    bulkChatworkDlg.sending
                  }
                >
                  {bulkChatworkDlg.testSending ? "テスト投稿中..." : "テスト投稿"}
                </button>

                <button
                  className="border px-3 py-1 rounded text-xs bg-red-600 text-white disabled:opacity-50"
                  onClick={runBulkChatworkSend}
                  disabled={
                    !bulkChatworkDlg.draftJobId ||
                    !bulkChatworkDlg.tested ||
                    bulkChatworkDlg.loading ||
                    bulkChatworkDlg.testSending ||
                    bulkChatworkDlg.sending ||
                    Number(bulkChatworkDlg.confirmCountInput || 0) !== bulkChatworkDlg.okCount
                  }
                >
                  {bulkChatworkDlg.sending ? "本投稿中..." : "本投稿"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rmsCredDlg.open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeRmsCredentialDialog} />
          <div className="relative w-[480px] max-w-[94vw] rounded-lg bg-white shadow-lg border p-4">
            <div className="text-sm font-semibold text-slate-500">認証情報（RMS / Yahoo RPA）</div>

            <div className="mt-2 text-xs text-slate-500 space-y-1">
              <div>
                対象: <b>{rmsCredDlg.shopUrl}</b>
              </div>
              <div className="text-slate-500">表示前に、現在ログイン中ユーザーのGoogleログインで再認証します。</div>
            </div>

            {!rmsCredDlg.verified ? (
              <div className="mt-4">
                <div className="text-xs text-slate-600">現在のGoogleログインで再認証を行います。</div>

                {rmsCredDlg.error && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                    {rmsCredDlg.error}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                    onClick={closeRmsCredentialDialog}
                    disabled={rmsCredDlg.busy}
                  >
                    キャンセル
                  </button>
                  <button
                    className="border px-3 py-1 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
                    disabled={rmsCredDlg.busy}
                    onClick={verifyAndLoadRmsCredentials}
                  >
                    {rmsCredDlg.busy ? "認証中..." : "Googleで再認証して表示"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <div className="mb-3 rounded border bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                  パスワード系は初期表示をマスクしています。必要なときだけコピー、または10秒表示してください。
                </div>

                <div className="space-y-4">
                  <div className="space-y-2 rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold text-slate-700">RMS系</div>
                    <div className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] gap-2 items-center">
                      <div className="text-xs font-semibold text-slate-700">rmsUser</div>
                      <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                        {rmsCredDlg.credentials?.rmsUser || "-"}
                      </div>
                      <button
                        className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                        onClick={() => copyRmsCredential("rmsUser")}
                        disabled={!String(rmsCredDlg.credentials?.rmsUser ?? "")}
                      >
                        {rmsCredDlg.copiedKey === "rmsUser" ? "コピー済" : "コピー"}
                      </button>
                    </div>
                    <div className="mt-1">
                      <a
                        href="https://glogin.rms.rakuten.co.jp/?sp_id=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-blue-700 underline font-semibold text-xs"
                      >
                        RMSログインはこちら
                      </a>
                    </div>

                    {rmsSecretRows.length === 0 ? (
                      <div className="text-xs text-slate-500">RMSパスワードの登録はありません。</div>
                    ) : (
                      rmsSecretRows.map((field) => {
                        const value = String(rmsCredDlg.credentials?.[field] ?? "");
                        const visible = !!rmsCredDlg.visibleSecrets[field];

                        return (
                          <div key={field} className="grid grid-cols-[7rem_minmax(0,1fr)_5rem_6rem] gap-2 items-center">
                            <div className="text-xs font-semibold text-slate-700">{field}</div>
                            <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                              {visible ? value : maskSecretKeepLast4(value)}
                            </div>
                            <button
                              className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                              onClick={() => copyRmsCredential(field)}
                            >
                              {rmsCredDlg.copiedKey === field ? "コピー済" : "コピー"}
                            </button>
                            <button
                              className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                              onClick={() => revealSecretTemporarily(field)}
                            >
                              {visible ? "表示中" : "10秒表示"}
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="space-y-2 rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold text-slate-700">CSV系</div>

                    <div className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] gap-2 items-center">
                      <div className="text-xs font-semibold text-slate-700">csvUser</div>
                      <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                        {rmsCredDlg.credentials?.csvUser || "-"}
                      </div>
                      <button
                        className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                        onClick={() => copyRmsCredential("csvUser")}
                        disabled={!String(rmsCredDlg.credentials?.csvUser ?? "")}
                      >
                        {rmsCredDlg.copiedKey === "csvUser" ? "コピー済" : "コピー"}
                      </button>
                    </div>

                    {csvSecretRows.length === 0 ? (
                      <div className="text-xs text-slate-500">CSVパスワードの登録はありません。</div>
                    ) : (
                      csvSecretRows.map((field) => {
                      const value = String(rmsCredDlg.credentials?.[field] ?? "");
                      const visible = !!rmsCredDlg.visibleSecrets[field];

                      return (
                        <div key={field} className="grid grid-cols-[7rem_minmax(0,1fr)_5rem_6rem] gap-2 items-center">
                          <div className="text-xs font-semibold text-slate-700">{field}</div>
                          <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                            {visible ? value : maskSecretKeepLast4(value)}
                          </div>
                          <button
                            className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                            onClick={() => copyRmsCredential(field)}
                          >
                            {rmsCredDlg.copiedKey === field ? "コピー済" : "コピー"}
                          </button>
                          <button
                            className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                            onClick={() => revealSecretTemporarily(field)}
                          >
                            {visible ? "表示中" : "10秒表示"}
                          </button>
                        </div>
                      );
                      })
                    )}
                  </div>

                  <div className="space-y-2 rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold text-slate-700">Yahoo（RPA用ログイン）</div>

                    {hasYahooShop && (
                      <div className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] gap-2 items-center">
                        <div className="text-xs font-semibold text-slate-700">紐づけアカウント</div>
                        <input
                          type="text"
                          className="border rounded px-2 py-1 text-sm font-mono"
                          value={yahooAccountKeyInput}
                          onChange={(e) => setYahooAccountKeyInput(e.target.value)}
                          placeholder="例: dev-bluecomblue"
                        />
                        <button
                          className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                          onClick={saveYahooAccountKey}
                          disabled={
                            yahooAccountSaving ||
                            !yahooAccountKeyInput.trim() ||
                            yahooAccountKeyInput.trim() === String(rmsCredDlg.credentials?.yahooAccountKey ?? "")
                          }
                        >
                          {yahooAccountSaving ? "保存中..." : "変更"}
                        </button>
                      </div>
                    )}
                    {hasYahooShop && (
                      <div className="text-[11px] text-slate-500">
                        この店舗のデータ取得に使うログインアカウント名。共通のRPA用IDに相乗りさせる場合はそのアカウント名を入力して変更
                      </div>
                    )}

                    {!hasYahooShop ? (
                      <div className="text-xs text-slate-500">Yahoo連携が未設定の店舗です（お客様の店舗登録後に表示されます）。</div>
                    ) : !hasYahooRpaCreds ? (
                      <div className="text-xs text-slate-500">このアカウントのログインID/PWは未登録です。</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] gap-2 items-center">
                          <div className="text-xs font-semibold text-slate-700">yahooRpaId</div>
                          <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                            {rmsCredDlg.credentials?.yahooRpaId || "-"}
                          </div>
                          <button
                            className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50 disabled:opacity-50"
                            onClick={() => copyRmsCredential("yahooRpaId")}
                            disabled={!String(rmsCredDlg.credentials?.yahooRpaId ?? "")}
                          >
                            {rmsCredDlg.copiedKey === "yahooRpaId" ? "コピー済" : "コピー"}
                          </button>
                        </div>
                        <div className="mt-1">
                          <a
                            href="https://pro.store.yahoo.co.jp/protop"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-blue-700 underline font-semibold text-xs"
                          >
                            ストアクリエイターProログインはこちら
                          </a>
                          <span className="ml-2 text-[11px] text-slate-500">
                            ※2段階認証オフの専用IDです。SMSは飛びません
                          </span>
                        </div>

                        {yahooSecretRows.map((field) => {
                          const value = String(rmsCredDlg.credentials?.[field] ?? "");
                          const visible = !!rmsCredDlg.visibleSecrets[field];

                          return (
                            <div key={field} className="grid grid-cols-[7rem_minmax(0,1fr)_5rem_6rem] gap-2 items-center">
                              <div className="text-xs font-semibold text-slate-700">{field}</div>
                              <div className="border rounded px-2 py-1 text-sm font-mono break-all bg-slate-50">
                                {visible ? value : maskSecretKeepLast4(value)}
                              </div>
                              <button
                                className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                                onClick={() => copyRmsCredential(field)}
                              >
                                {rmsCredDlg.copiedKey === field ? "コピー済" : "コピー"}
                              </button>
                              <button
                                className="border px-2 py-1 rounded text-[11px] bg-white hover:bg-gray-50"
                                onClick={() => revealSecretTemporarily(field)}
                              >
                                {visible ? "表示中" : "10秒表示"}
                              </button>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>

                {rmsCredDlg.error && (
                  <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                    {rmsCredDlg.error}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                    onClick={closeRmsCredentialDialog}
                  >
                    閉じる
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {historyDlg.open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeRmsHistory} />
          <div className="relative w-[860px] max-w-[94vw] max-h-[85vh] flex flex-col rounded-lg bg-white shadow-lg border p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">取得停止の経緯</div>
                <div className="mt-0.5 text-xs text-slate-600">
                  {historyDlg.companyName ? `${historyDlg.companyName} / ` : ""}
                  {historyDlg.shopUrl}
                </div>
              </div>
              <button className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50" onClick={closeRmsHistory}>
                閉じる
              </button>
            </div>

            <div className="mt-1 text-[11px] text-slate-500">
              新しい順・最大50件。記録は 2026年10月の改修（RN-285）のあとの出来事から残ります
            </div>

            <div className="mt-3 overflow-auto">
              {historyDlg.loading ? (
                <div className="text-xs text-slate-600">読み込み中...</div>
              ) : historyDlg.error ? (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{historyDlg.error}</div>
              ) : historyDlg.items.length === 0 ? (
                <div className="text-xs text-slate-600">記録はまだありません</div>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600">
                      <th className="border px-2 py-1 text-left whitespace-nowrap">日時</th>
                      <th className="border px-2 py-1 text-left whitespace-nowrap">出来事</th>
                      <th className="border px-2 py-1 text-left whitespace-nowrap">誰が</th>
                      <th className="border px-2 py-1 text-left">ひとこと</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyDlg.items.map((it) => (
                      <tr key={`${it.companyId ?? ""}-${it.id}`} className="align-top">
                        <td className="border px-2 py-1 whitespace-nowrap text-slate-700">{toYmdHm(it.at) || "-"}</td>
                        <td
                          className={`border px-2 py-1 whitespace-nowrap font-medium ${
                            it.type === "rmsAuthStopped"
                              ? "text-red-700"
                              : it.type === "rmsAuthResumed"
                                ? "text-emerald-700"
                                : "text-slate-800"
                          }`}
                        >
                          {rmsHistoryTitle(it)}
                        </td>
                        <td className="border px-2 py-1 whitespace-nowrap text-slate-700">{it.byEmail || "自動（取得）"}</td>
                        <td className="border px-2 py-1 text-slate-600">
                          {rmsHistoryDetail(it)}
                          {it.companyId && it.companyId !== historyDlg.companyId && (
                            <span className="ml-1 text-[11px] text-slate-400">（同じ店舗URLの別の会社の記録）</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteDlg.open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeDelete} />
          <div className="relative w-[560px] max-w-[92vw] rounded-lg bg-white shadow-lg border p-4">
            <div className="text-sm font-semibold text-slate-900">会社（ショップ）削除</div>

            <div className="mt-2 text-xs text-slate-700 space-y-1">
              <div>
                対象: <b>{deleteDlg.shopUrl}</b>
              </div>
              <div className="text-red-700">※ 危険操作です。削除すると元に戻せません。</div>
            </div>

            <div className="mt-3">
              <div className="text-xs text-slate-600 mb-1">
                削除を実行するには、下に <b>ショップURL</b> をそのまま入力してください
              </div>
              <input
                className="border px-2 py-1 rounded w-full text-sm"
                placeholder={deleteDlg.shopUrl}
                value={deleteDlg.input}
                onChange={(e) => setDeleteDlg((p) => ({ ...p, input: e.target.value, error: "" }))}
                onKeyDown={onDeleteInputKeyDown}
                autoFocus
              />
              <div className="mt-1 text-[11px] text-slate-500">
                一致判定:{" "}
                {String(deleteDlg.input).trim() === String(deleteDlg.shopUrl).trim() ? (
                  <span className="text-emerald-700 font-semibold">OK</span>
                ) : (
                  <span className="text-amber-700">未一致</span>
                )}
              </div>

              {deleteDlg.error && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {deleteDlg.error}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                onClick={closeDelete}
                disabled={deleteDlg.busy}
              >
                キャンセル
              </button>
              <button
                className="border px-3 py-1 rounded text-xs bg-red-600 text-white disabled:opacity-50"
                disabled={deleteDlg.busy || String(deleteDlg.input).trim() !== String(deleteDlg.shopUrl).trim()}
                onClick={runDelete}
                title="ショップURLが一致した場合のみ削除できます"
              >
                {deleteDlg.busy ? "削除中..." : "削除実行"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}